import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BookOpen,
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileText,
  FileUp,
  GraduationCap,
  LayoutDashboard,
  Library,
  LogIn,
  Plus,
  Printer,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import {
  AgentRun,
  AutomationControlPlane,
  AutomationPolicy,
  AutomationProfile,
  AutomationSettings,
  api,
  ApiError,
  ImportBatch,
  Knowledge,
  MaintenanceSpace,
  Paper,
  PaperDetail,
  post,
  Question,
  QuestionSource,
  Scope,
  Summary,
  TeachingClassDetail,
  TeachingClassWorkspace,
  TeachingPlanDetail,
} from "./api";
import "./styles.css";

type Page =
  | "home"
  | "classes"
  | "knowledge"
  | "questions"
  | "imports"
  | "assessments"
  | "exams"
  | "grading"
  | "learning"
  | "automation";
type SessionUser = { id:string; nickname:string; role:string; campus?:{id:string;name:string}|null; globalCampusAccess:boolean; permissions:string[] };
const navGroups: Array<{ label: string; items: Array<{ id: Page; label: string; icon: React.ElementType }> }> = [
  { label: "生产控制", items: [
    { id: "home", label: "自动化总控", icon: LayoutDashboard },
    { id: "automation", label: "审批与异常", icon: ShieldCheck },
  ] },
  { label: "生产对象", items: [
    { id: "classes", label: "教学班配置", icon: Users },
    { id: "assessments", label: "课堂小测", icon: FileText },
    { id: "exams", label: "考卷管理", icon: ClipboardCheck },
    { id: "grading", label: "批阅回流", icon: ClipboardCheck },
    { id: "learning", label: "学情数据库", icon: GraduationCap },
  ] },
  { label: "生产资料", items: [
    { id: "knowledge", label: "知识体系", icon: BookOpen },
    { id: "questions", label: "题库治理", icon: Library },
    { id: "imports", label: "题源接入", icon: FileUp },
  ] },
];
const pages = navGroups.flatMap((group) => group.items);
const currentAcademicYear = () => {
  const now = new Date();
  const start = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}—${start + 1} 学年`;
};
const statusLabel: Record<string, string> = {
  active: "正式",
  draft: "草稿",
  review: "待审校",
  rejected: "已退回",
  approved: "已批准",
  ready: "可处理",
  grading: "批阅中",
  published: "已发布",
  printed: "已打印",
  closed: "已结卷",
  cancelled: "已取消",
  queued: "排队中",
  running: "处理中",
  retry: "等待重试",
  waiting_approval: "待审批",
  completed: "已完成",
  failed: "失败",
  applied: "已入库",
  matched: "已匹配",
  validating: "校验中",
  committing: "入库中",
  blocked: "已阻止",
  verified: "已核验",
  retired: "已停用",
  valid: "有效",
  invalid: "有错误",
  duplicate: "重复",
  imported: "已导入",
};
const fmtDate = (value?: string) =>
  value
    ? new Date(value).toLocaleString("zh-CN", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

function useToken() {
  const [token, setToken] = useState(
    () => localStorage.getItem("teaching_engine_token") || "",
  );
  const save = (value: string) => {
    setToken(value);
    if (value) localStorage.setItem("teaching_engine_token", value);
    else localStorage.removeItem("teaching_engine_token");
  };
  return [token, save] as const;
}
function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="empty">
      <BookOpen />
      <p>{children}</p>
    </div>
  );
}
function Status({ value }: { value: string }) {
  return (
    <span className={`status status-${value}`}>
      {statusLabel[value] || value}
    </span>
  );
}
function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <section className="modal">
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function App() {
  const [token, setToken] = useToken();
  const [page, setPage] = useState<Page>(
    () => (location.hash.slice(1) as Page) || "home",
  );
  const [toast, setToast] = useState("");
  const [classFocus, setClassFocus] = useState("");
  const [studentFocus, setStudentFocus] = useState("");
  const [testPersona, setTestPersona] = useState<"author" | "reviewer" | "">(
    () => (localStorage.getItem("teaching_test_persona") as "author" | "reviewer" | "") || "",
  );
  const [ssoBusy, setSsoBusy] = useState(() => location.hash.startsWith("#sso="));
  const authRecoveryAttempted = useRef(false);
  const qc = useQueryClient();
  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: () => api<{ summary: Summary }>("/api/teaching-engine/overview"),
    refetchInterval: 30000,
  });
  const scopes = useQuery({
    queryKey: ["scopes"],
    queryFn: () => api<{ items: Scope[] }>("/api/teaching-engine/taxonomy"),
  });
  const classes = useQuery({
    queryKey: ["classes", token],
    enabled: !!token,
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string; campus_name?: string }> }>(
        "/api/teaching-engine/workspace/classes?limit=100&offset=0&scope=all",
        { token },
      ),
  });
  const login = useMutation({
    mutationFn: async (persona: "author" | "reviewer") => {
      if (token) await api("/api/auth/logout", { method: "POST", token }).catch(() => undefined);
      return api<{ token: string; persona: "author" | "reviewer"; user: { nickname: string } }>(
        `/api/auth/test-bypass?persona=${persona}`, { method: "POST" });
    },
    onSuccess: (d) => {
      setToken(d.token);
      setTestPersona(d.persona);
      localStorage.setItem("teaching_test_persona", d.persona);
      setToast(`已连接：${d.user.nickname}`);
      qc.invalidateQueries();
    },
    onError: (e) => setToast((e as Error).message),
  });
  const me = useQuery({
    queryKey: ["teaching-session", token], enabled: !!token,
    queryFn: () => api<{user:SessionUser}>("/api/auth/me", { token }),
    retry: false,
  });
  useEffect(() => {
    if (me.data?.user.id) authRecoveryAttempted.current = false;
  }, [me.data?.user.id]);
  useEffect(() => {
    if (!token || !me.isError || authRecoveryAttempted.current) return;
    const error = me.error;
    if (!(error instanceof ApiError) || ![401, 403].includes(error.status)) return;
    authRecoveryAttempted.current = true;
    const persona = testPersona || "author";
    setToken("");
    qc.removeQueries({ queryKey: ["teaching-session"] });
    setToast("账号会话已失效，正在自动重新连接");
    login.mutate(persona);
  }, [me.isError, me.error, token, testPersona]);
  useEffect(() => {
    const raw = location.hash.startsWith("#sso=") ? location.hash.slice(5) : "";
    if (!raw) return;
    history.replaceState(null, "", `${location.pathname}${location.search}#home`);
    setPage("home");
    api<{token:string;user:SessionUser}>("/api/auth/engine-exchange", {
      method: "POST", body: JSON.stringify({ ticket: decodeURIComponent(raw) }),
    }).then((data) => { setToken(data.token); setTestPersona(""); localStorage.removeItem("teaching_test_persona"); setToast(`已进入：${data.user.nickname}`); qc.invalidateQueries(); })
      .catch((error) => setToast(error instanceof Error ? error.message : "教学引擎登录失败"))
      .finally(() => setSsoBusy(false));
  }, []);
  useEffect(() => {
    location.hash = page;
  }, [page]);
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(""), 3200);
      return () => clearTimeout(t);
    }
  }, [toast]);
  const summary = overview.data?.summary;
  const go = (target: Page, classId = "", studentId = "") => {
    setClassFocus(classId);
    setStudentFocus(studentId);
    setPage(target);
  };
  return (
    <div className="shell">
      <a className="skip-link" href="#teaching-main">跳到主要内容</a>
      <aside className="sidebar">
        <div className="brand">
          <div className="seal">教</div>
          <div>
            <strong>时代教学引擎</strong>
            <small>教学自动化生产控制中心</small>
          </div>
        </div>
        <nav aria-label="教学引擎主导航">
          {navGroups.map((group) => {
            const activeItem = group.items.find(item => item.id === page);
            const GroupIcon = (activeItem || group.items[0]).icon;
            return <details className={`nav-group ${activeItem ? "active" : ""}`} key={group.label}>
              <summary><GroupIcon /><span>{activeItem?.label || group.label}</span><ChevronDown /></summary>
              <div className="nav-menu">
                <header>{group.label}</header>
                {group.items.map((item) => { const Icon = item.icon; return <button key={item.id}
                  aria-current={page === item.id ? "page" : undefined} className={page === item.id ? "active" : ""}
                  onClick={(event) => { go(item.id); event.currentTarget.closest("details")?.removeAttribute("open"); }}><Icon /><span>{item.label}</span>
                  {item.id === "automation" && summary?.pending_approvals ? <b>{summary.pending_approvals}</b> : null}</button>; })}
              </div>
            </details>;
          })}
        </nav>
        <div className="side-note">
          <b>当前学期</b>
          <span>{currentAcademicYear()}</span>
          <a href="https://campus.agekings.com">
            返回校区管理 <ChevronRight />
          </a>
        </div>
      </aside>
      <main id="teaching-main">
        <header className="topbar">
          <div>
            <span className="crumb">
              教学引擎 / {pages.find((x) => x.id === page)?.label}
            </span>
            <h1>{pages.find((x) => x.id === page)?.label}</h1>
          </div>
          <div className="top-actions">
            <span className={`health ${overview.isError ? "bad" : ""}`}>
              <i />
              {overview.isError ? "服务异常" : "数据已同步"}
            </span>
            <button className="quiet" onClick={() => qc.invalidateQueries()}>
              <RefreshCw />
              刷新
            </button>
            {token ? (
              <div className={`user ${me.isError ? "user-error" : ""}`}><GraduationCap />
                {me.data?.user.nickname || (me.isError ? "账号已失效，正在重连" : "正在读取账号")}</div>
            ) : (
              <button
                className="primary"
                onClick={() => login.mutate("author")}
                disabled={login.isPending || ssoBusy}
              >
                <LogIn />
                {ssoBusy ? "正在进入" : login.isPending ? "连接中" : "测试模式连接"}
              </button>
            )}
          </div>
        </header>
        <div className="content">
          {page === "home" && (
            <ProductionControl token={token} allCampuses={!!token} go={(target) => go(target)} notify={setToast} />
          )}{" "}
          {page === "classes" && (
            <ClassWorkspace token={token} allCampuses={!!token} go={go} />
          )}{" "}
          {page === "knowledge" && (
            <KnowledgePage scopes={scopes.data?.items || []} token={token} />
          )}{" "}
          {page === "questions" && (
            <QuestionPage
              scopes={scopes.data?.items || []}
              token={token}
              notify={setToast}
            />
          )}{" "}
          {page === "imports" && (
            <ImportPage scopes={scopes.data?.items || []} token={token} notify={setToast} />
          )}{" "}
          {page === "assessments" && (
            <AssessmentPage
              scopes={scopes.data?.items || []}
              classes={classes.data?.items || []}
              token={token}
              allCampuses={!!token}
              classFocus={classFocus}
              clearClassFocus={() => setClassFocus("")}
              notify={setToast}
            />
          )}{" "}
          {page === "exams" && <ExamCenter scopes={scopes.data?.items || []} classes={classes.data?.items || []}
            token={token} notify={setToast} />}{" "}
          {page === "grading" && <GradingPage token={token} classFocus={classFocus} clearClassFocus={() => setClassFocus("")} notify={setToast} />}{" "}
          {page === "learning" && <LearningPage token={token} initialStudent={studentFocus} notify={setToast} />}{" "}
          {page === "automation" && (
            <AutomationPage token={token} allCampuses={!!token} notify={setToast} />
          )}
        </div>
      </main>
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

const workflowStageLabel: Record<string, string> = {
  schedule_resolved: "已解析课次", scope_resolution: "定位课程范围", question_generation: "生成小测",
  human_review: "等待审批", dispatch_ready: "等待下发", waiting_for_class: "已下发，等待上课",
  waiting_for_grading: "已打印，等待批阅", grading_ingest: "成绩回流", grading_review: "批阅复核",
  learning_profile_updated: "学情已更新", generation_failed: "生成失败", scope_resolution_failed: "课程范围异常",
  knowledge_resolution_failed: "知识点异常", retry_requested: "等待重试", cancelled_by_user: "已人工取消",
};
const weekdayLabel = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
const signedAxisPresets = [
  { value: -0.8, label: "极低" }, { value: -0.6, label: "最低" }, { value: -0.4, label: "低" },
  { value: -0.2, label: "稍低" }, { value: 0, label: "同等" }, { value: 0.2, label: "稍高" },
  { value: 0.4, label: "高" }, { value: 0.6, label: "最高" }, { value: 0.8, label: "极高" },
];
const knowledgeWeightPresets = [
  { value: 0, label: "完全一致" }, { value: 0.2, label: "高度命中" }, { value: 0.4, label: "较高命中" },
  { value: 0.6, label: "部分命中" }, { value: 0.8, label: "弱命中" }, { value: 1, label: "允许不命中" },
];
const absoluteWeightPresets = [
  { value: .1, label: "补充知识点" }, { value: .3, label: "次要知识点" }, { value: .5, label: "协同知识点" },
  { value: .7, label: "重要知识点" }, { value: 1, label: "核心知识点" },
];
const absoluteFrequencyPresets = [
  { value: .1, label: "极少见题型" }, { value: .25, label: "少见题型" }, { value: .5, label: "一般题型" },
  { value: .75, label: "常见题型" }, { value: .9, label: "高频题型" },
];
const absoluteDifficultyPresets = [
  { value: .1, label: "入门难度" }, { value: .25, label: "基础难度" }, { value: .5, label: "中等难度" },
  { value: .75, label: "较高难度" }, { value: .9, label: "高难度" },
];

function ProductionControl({ token, allCampuses, go, notify }: { token: string; allCampuses: boolean; go: (p: Page) => void; notify: (x: string) => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AutomationPolicy | null>(null);
  const [editingProfile, setEditingProfile] = useState<AutomationProfile | null>(null);
  const [newPreset, setNewPreset] = useState(false);
  const [assigning, setAssigning] = useState<AutomationProfile | null>(null);
  const control = useQuery({
    queryKey: ["automation-control-plane", token], enabled: !!token,
    queryFn: () => api<AutomationControlPlane>(`/api/teaching-engine/automation/control-plane${allCampuses ? "?scope=all" : ""}`, { token }),
    refetchInterval: 15000,
  });
  const settings = useQuery({
    queryKey: ["automation-settings", token, allCampuses], enabled: !!token,
    queryFn: () => api<AutomationSettings>(`/api/teaching-engine/automation/settings${allCampuses ? "?scope=all" : ""}`, { token }),
  });
  const saveProfile = useMutation({
    mutationFn: ({ profile, draft }: { profile?: AutomationProfile; draft: Record<string, any> }) =>
      api(`/api/teaching-engine/automation/profiles${profile ? `/${profile.id}` : ""}${allCampuses ? "?scope=all" : ""}`, {
        method: profile ? "PUT" : "POST", token, body: JSON.stringify(draft),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automation-settings"] }); qc.invalidateQueries({ queryKey: ["automation-control-plane"] });
      setEditingProfile(null); setNewPreset(false); notify("自动化设置已保存并同步到继承班级"); },
    onError: (error) => notify((error as Error).message),
  });
  const assignProfile = useMutation({
    mutationFn: ({ profile, slots }: { profile: AutomationProfile; slots: string[] }) => post(
      `/api/teaching-engine/automation/profiles/${profile.id}/assign${allCampuses ? "?scope=all" : ""}`,
      { schedule_slot_ids: slots }, token),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automation-settings"] }); qc.invalidateQueries({ queryKey: ["automation-control-plane"] });
      setAssigning(null); notify("预设已批量应用到所选教学班"); },
    onError: (error) => notify((error as Error).message),
  });
  const policy = useMutation({
    mutationFn: ({ item, patch }: { item: AutomationPolicy; patch: Record<string, unknown> }) =>
      api(`/api/teaching-engine/automation/policies/${item.schedule_slot_id}${allCampuses ? "?scope=all" : ""}`, {
        method: "PUT", token, body: JSON.stringify({
          enabled: item.enabled, generation_mode: item.generation_mode, trigger_days_before: item.trigger_days_before,
          trigger_local_time: item.trigger_local_time, timezone: item.timezone, question_count: item.question_count,
          wrong_question_count: item.wrong_question_count, difficulty_offset: item.difficulty_offset,
          foundation_offset: item.foundation_offset, frequency_offset: item.frequency_offset,
          knowledge_strategy: item.knowledge_strategy, knowledge_point_ids: item.knowledge_point_ids || [],
          approval_required: item.approval_required, auto_dispatch_after_approval: item.auto_dispatch_after_approval,
          print_copies: item.print_copies, ...patch,
        }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automation-control-plane"] }); notify("自动化策略已保存"); },
    onError: (error) => notify((error as Error).message),
  });
  const workflowAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "retry" | "cancel" }) => post(
      `/api/teaching-engine/automation/workflows/${id}/${action}${allCampuses ? "?scope=all" : ""}`,
      { reason: action === "retry" ? "管理员从生产总控请求重试" : "管理员从生产总控取消" }, token),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automation-control-plane"] }); notify("工作流状态已更新"); },
    onError: (error) => notify((error as Error).message),
  });
  if (!token) return <Empty>请从校区管理平台进入教学引擎</Empty>;
  if (control.isLoading || settings.isLoading) return <Empty>正在读取自动化生产线</Empty>;
  if (control.isError || settings.isError) return <Empty>{((control.error || settings.error) as Error).message}</Empty>;
  const data = control.data!;
  const setup = settings.data!;
  const metrics = [
    ["已启用规则", data.summary.enabled_policies, "来自校区排课", Activity],
    ["运行中", data.summary.running, "生成、下发或分析", RefreshCw],
    ["待审批", data.summary.approval_pending, "人工决策闸门", ShieldCheck],
    ["已备课", data.summary.ready_for_class, "已进入打印/课堂链路", CheckCircle2],
    ["异常", data.summary.exceptions, "可重试或人工处理", AlertTriangle],
  ] as const;
  return <>
    <section className="control-hero">
      <div><p>教学自动化生产控制中心</p><h2>从排课触发，到学情回写的完整生产线</h2>
        <span>校区平台负责配置业务；教学引擎按课次自动运行。这里只管理规则、审批、产物、异常和质量。</span></div>
      <button className="primary" onClick={() => go("automation")}><ShieldCheck />处理审批与异常</button>
    </section>
    <section className="automation-hierarchy">
      <header><div><h3>自动化设置体系</h3><p>按“系统默认 → 校区规则 → 策略预设 → 班级例外”继承；只在确有差异时覆盖。</p></div>
        <button className="primary small" onClick={() => setNewPreset(true)}><Plus />创建策略预设</button></header>
      <div className="hierarchy-grid">
        <article className="hierarchy-card system"><span className="hierarchy-level">01 · 系统默认</span>
          <h4>{setup.system?.name || "尚未建立系统默认"}</h4>
          <p>{setup.system?.description || "为所有校区提供统一的安全基线。"}</p>
          {setup.system && <div className="profile-facts"><span>提前 {setup.system.settings.trigger_days_before} 天</span><span>{setup.system.settings.question_count} 题</span><span>{setup.system.settings.approval_required ? "必须审批" : "免审批"}</span></div>}
          {setup.system && <button className="text-action" onClick={() => setEditingProfile(setup.system!)}>编辑系统默认</button>}
        </article>
        <article className="hierarchy-card"><span className="hierarchy-level">02 · 校区规则</span>
          <h4>{setup.campuses.length} 个校区</h4><p>每个校区可在系统默认之上建立自己的长期规则。</p>
          <div className="compact-profile-list">{setup.campuses.map(campus => { const profile = setup.campus_profiles.find(x => x.campus_id === campus.id); return <button key={campus.id}
            onClick={() => profile ? setEditingProfile(profile) : setEditingProfile({ id: "", campus_id: campus.id, campus_name: campus.name, profile_type: "campus", name: `${campus.name}默认规则`, description: "该校区教学自动化默认规则", settings: setup.system!.settings, assigned_count: 0, updated_at: "" })}>
            <b>{campus.name}</b><small>{profile ? `${profile.assigned_count} 个班级继承` : "沿用系统默认 · 点击建立"}</small></button>; })}</div>
        </article>
        <article className="hierarchy-card"><span className="hierarchy-level">03 · 策略预设</span>
          <h4>{setup.presets.length} 套可复用预设</h4><p>例如中考冲刺、日常巩固、个性化错题，可批量分配给多个班。</p>
          <div className="compact-profile-list">{setup.presets.slice(0, 4).map(profile => <div key={profile.id}><button onClick={() => setEditingProfile(profile)}><b>{profile.name}</b><small>{profile.campus_name || "全局"} · {profile.assigned_count} 个班</small></button><button className="text-action" onClick={() => setAssigning(profile)}>应用</button></div>)}
            {!setup.presets.length && <small>尚无预设，创建后可一键应用到多个班级。</small>}</div>
        </article>
        <article className="hierarchy-card exception"><span className="hierarchy-level">04 · 班级例外</span>
          <h4>{setup.campuses.reduce((sum, x) => sum + x.exception_count, 0)} 个例外</h4><p>仅保留确实不同的班级设置；下表的“设置”会建立班级例外。</p>
          <div className="profile-facts"><span>可追溯来源</span><span>随时恢复继承</span></div>
        </article>
      </div>
    </section>
    <div className="control-metrics">{metrics.map(([label, value, note, Icon]) => <article key={label}>
      <span><Icon />{label}</span><strong>{value}</strong><small>{note}</small></article>)}</div>
    <section className="pipeline-panel">
      <header><div><h3>课前小测生产线</h3><p>每个课次都有独立幂等工作流；服务重启不会重复生成或重复下发。</p></div><span className="live-dot">15 秒刷新</span></header>
      <div className="pipeline-flow">{[
        ["1", "排课触发", "提前 N 天固定时间"], ["2", "智能组题", "错题坐标与知识点"],
        ["3", "人工审批", "批准、退回或拒绝"], ["4", "自动下发", "办公室打印队列"],
        ["5", "课堂批阅", "二维码时效工具"], ["6", "学情回写", "成绩、照片与薄弱点"],
      ].map(([n, title, note], index) => <React.Fragment key={n}><article><b>{n}</b><strong>{title}</strong><small>{note}</small></article>{index < 5 && <ChevronRight />}</React.Fragment>)}</div>
    </section>
    <div className="control-grid">
      <section className="paper-panel policy-panel"><header><div><h3>班级生效规则与例外</h3><p>先看规则来源；逐班修改只用于特殊情况。</p></div></header>
        <div className="control-table-wrap"><table className="control-table"><thead><tr><th>课程与课次</th><th>规则来源</th><th>触发</th><th>生成方式</th><th>错题偏移</th><th>最近运行</th><th>启用</th><th></th></tr></thead>
          <tbody>{data.policies.map(item => <tr key={item.id}><td><b>{item.course_name}</b><small>{item.class_name} · {item.teacher_name} · {item.place_name}</small></td>
            <td><span className={`inheritance-badge ${item.inheritance_mode}`}>{item.inheritance_mode === "system" ? "系统默认" : item.inheritance_mode === "campus" ? "校区规则" : item.inheritance_mode === "preset" ? item.profile_name || "策略预设" : "班级例外"}</span></td>
            <td>提前 {item.trigger_days_before} 天 {item.trigger_local_time}<small>{weekdayLabel[item.weekday]} {item.start_time} 上课</small></td>
            <td>{item.config?.generation_basis === "knowledge_composition" ? "知识点新题" : item.generation_mode === "personalized" ? "每生错题再生" : "全班错题再生"}<small>{item.question_count} 题 · {item.config?.generation_basis === "knowledge_composition" ? `${item.config.new_question_axes?.length || 0} 个知识点混合` : `错题迁移 ${item.wrong_question_count} 题`}</small></td>
            <td>难度 {formatSignedPercent(item.difficulty_offset)}<small>知识偏离 {Math.round(item.foundation_offset * 100)}% · 频率 {formatSignedPercent(item.frequency_offset)}</small></td>
            <td>{item.latest_status ? <Status value={item.latest_status} /> : "尚未运行"}<small>{item.latest_stage ? workflowStageLabel[item.latest_stage] || item.latest_stage : "—"}</small></td>
            <td><button className={`policy-switch ${item.enabled ? "on" : ""}`} aria-label={item.enabled ? "停用" : "启用"} onClick={() => policy.mutate({ item, patch: { enabled: !item.enabled } })}><i /></button></td>
            <td><button className="text-action" onClick={() => setEditing(item)}>设置</button></td></tr>)}
            {!data.policies.length && <tr><td colSpan={8}><Empty>请先在校区排课中为课程启用“课前小测”工具</Empty></td></tr>}</tbody></table></div>
      </section>
      <section className="paper-panel workflow-panel"><header><div><h3>最近生产实例</h3><p>按真实课次显示当前阶段，而不是只陈列零散任务。</p></div></header>
        <div className="workflow-list">{data.workflows.slice(0, 12).map(item => <article key={item.id}>
          <div className={`workflow-rail status-${item.status}`} /><div><b>{item.lesson_date} · {item.course_name}</b><span>{item.class_name} · {workflowStageLabel[item.current_stage] || item.current_stage}</span><small>更新 {fmtDate(item.updated_at)}</small></div>
          <Status value={item.status} />{["failed", "retry"].includes(item.status) && <button className="text-action" onClick={() => workflowAction.mutate({ id: item.id, action: "retry" })}>重试</button>}
          {!['completed','cancelled','failed'].includes(item.status) && <button className="text-action danger" onClick={() => workflowAction.mutate({ id: item.id, action: "cancel" })}>取消</button>}
        </article>)}{!data.workflows.length && <Empty>策略触发后，课次工作流会出现在这里</Empty>}</div>
      </section>
    </div>
    {editing && <PolicyEditor item={editing} token={token} pending={policy.isPending} onClose={() => setEditing(null)} onSave={(patch) => policy.mutate({ item: editing, patch }, { onSuccess: () => setEditing(null) })} />}
    {(editingProfile || newPreset) && <ProfileEditor profile={editingProfile || undefined} system={setup.system} campuses={setup.campuses}
      pending={saveProfile.isPending} onClose={() => { setEditingProfile(null); setNewPreset(false); }}
      onSave={(draft) => saveProfile.mutate({ profile: editingProfile?.id ? editingProfile : undefined, draft })} />}
    {assigning && <ProfileAssignment profile={assigning} policies={data.policies} pending={assignProfile.isPending}
      onClose={() => setAssigning(null)} onSave={(slots) => assignProfile.mutate({ profile: assigning, slots })} />}
  </>;
}

function PolicyEditor({ item, token, pending, onClose, onSave }: { item: AutomationPolicy; token: string; pending: boolean; onClose: () => void; onSave: (patch: Record<string, unknown>) => void }) {
  const [form, setForm] = useState<any>({ ...item, generation_basis: item.config?.generation_basis || "wrong_regeneration",
    new_question_axes: item.config?.new_question_axes || [], expand_neighbor_knowledge: item.config?.expand_neighbor_knowledge || false,
    neighbor_knowledge_limit: item.config?.neighbor_knowledge_limit ?? 2 });
  const knowledge = useQuery({ queryKey: ["automation-knowledge", item.scope_id], enabled: !!item.scope_id && !!token,
    queryFn: () => api<{items:Array<{id:string;code:string;name:string;importance:number}>}>(`/api/teaching-engine/knowledge-points?scope_id=${item.scope_id}`, { token }) });
  return <Modal title="课前小测自动化策略" subtitle={`${item.class_name} · ${item.course_name} · ${weekdayLabel[item.weekday]} ${item.start_time}`} onClose={onClose}>
    <StrategySettings form={form} setForm={setForm} knowledgeOptions={knowledge.data?.items || []} allowComposition />
    <footer className="modal-actions"><button className="quiet" onClick={onClose}>取消</button><button className="primary" disabled={pending || form.wrong_question_count > form.question_count || (form.generation_basis === "knowledge_composition" && !form.new_question_axes?.length)} onClick={() => onSave(form)}>{pending ? "保存中" : "保存策略"}</button></footer>
  </Modal>;
}

function StrategySettings({ form, setForm, showEnabled = false, knowledgeOptions = [], allowComposition = false }: {
  form: any; setForm: (value: any) => void; showEnabled?: boolean;
  knowledgeOptions?: Array<{id:string;code:string;name:string;importance:number}>; allowComposition?: boolean;
}) {
  const change = (patch: Record<string, unknown>) => setForm({ ...form, ...patch });
  return <div className="settings-sheet">
    <section className="settings-section">
      <header><span>01</span><div><h3>触发与生成</h3><p>决定何时生产、为谁生成以及试卷基本规格。</p></div></header>
      <div className="settings-grid">
        <label>生成范围<select value={form.generation_mode} onChange={e => change({ generation_mode: e.target.value })}><option value="class">全班统一一份</option><option value="personalized">按学生个性化</option></select><small>个性化模式会依据每名学生的薄弱点出题</small></label>
        <label>提前天数<input type="number" min={0} max={14} value={form.trigger_days_before} onChange={e => change({ trigger_days_before: Number(e.target.value) })} /><small>相对于正式上课时间提前生产</small></label>
        <label>固定触发时间<input type="time" value={form.trigger_local_time} onChange={e => change({ trigger_local_time: e.target.value })} /><small>按校区时区执行</small></label>
        <label>总题数<input type="number" min={1} max={20} value={form.question_count} onChange={e => change({ question_count: Number(e.target.value) })} /></label>
        <label>错题变式题数<input type="number" min={0} max={form.question_count} value={form.wrong_question_count} onChange={e => change({ wrong_question_count: Number(e.target.value) })} /><small>不能超过试卷总题数</small></label>
        <label>打印份数<input type="number" min={1} max={5000} value={form.print_copies} onChange={e => change({ print_copies: Number(e.target.value) })} /></label>
      </div>
    </section>
    <section className="settings-section">
      <header><span>02</span><div><h3>知识点与错题自适应</h3><p>围绕最近错题坐标调整下一次题目的命中区域。</p></div></header>
      {allowComposition && <div className="generation-basis-picker">
        <button type="button" className={(form.generation_basis || "wrong_regeneration") === "wrong_regeneration" ? "active" : ""} onClick={() => change({ generation_basis: "wrong_regeneration" })}><b>错题再生</b><small>以历史错题为锚点，使用相对百分比偏移</small></button>
        <button type="button" className={form.generation_basis === "knowledge_composition" ? "active" : ""} onClick={() => change({ generation_basis: "knowledge_composition" })}><b>知识点新题</b><small>以题库整体坐标为锚点，最多组合 10 个知识点</small></button>
      </div>}
      {(!allowComposition || form.generation_basis !== "knowledge_composition") ? <div className="settings-grid strategy-grid">
        <label className="wide">知识点来源<select value={form.knowledge_strategy} onChange={e => change({ knowledge_strategy: e.target.value })}><option value="class_weakness">全班薄弱点</option><option value="student_weakness">学生个人薄弱点</option><option value="teaching_plan">教学计划进度</option><option value="manual">固定知识点</option></select></label>
        <AxisPresetControl label="知识点权重偏移" value={Math.max(0, Number(form.foundation_offset) || 0)} min={0} presets={knowledgeWeightPresets}
          onChange={value => change({ foundation_offset: value })} help="0% 完全命中原知识点，100% 允许完全不命中" />
        <AxisPresetControl label="题型频率偏移" value={Number(form.frequency_offset) || 0} min={-1} presets={signedAxisPresets.map(item => ({ ...item, label: `${item.label}频率` }))}
          onChange={value => change({ frequency_offset: value })} help="0% 与原题频率最接近，负值更少见，正值更常见" />
        <AxisPresetControl label="题目难度偏移" value={Number(form.difficulty_offset) || 0} min={-1} presets={signedAxisPresets.map(item => ({ ...item, label: `${item.label}难度` }))}
          onChange={value => change({ difficulty_offset: value })} help="0% 与原题难度相同，负值更简单，正值更困难" />
      </div> : <KnowledgeComposition form={form} change={change} options={knowledgeOptions} />}
      <div className="coordinate-help"><b>使用说明</b><span>{form.generation_basis === "knowledge_composition" ? "新题使用题库整体统计的绝对坐标；多个知识点会共同参与混合题检索与生成。" : "日常直接选择中文预设；只有需要精确控制时才选择“自定义微调”并拖动百分比滑杆。"}</span></div>
    </section>
    <section className="settings-section">
      <header><span>03</span><div><h3>审批与下发</h3><p>控制生成后的人工把关、打印队列和课堂链路。</p></div></header>
      <div className="toggle-list">
        {showEnabled && <label><span><b>启用课前小测</b><small>继承此规则的班级会按排课自动运行</small></span><input type="checkbox" checked={form.enabled} onChange={e => change({ enabled: e.target.checked })} /></label>}
        <label><span><b>生成后人工审批</b><small>试卷必须确认内容后才能正式下发</small></span><input type="checkbox" checked={form.approval_required} onChange={e => change({ approval_required: e.target.checked })} /></label>
        <label><span><b>审批后自动下发</b><small>通过后自动进入打印队列并同步课堂批阅工具</small></span><input type="checkbox" checked={form.auto_dispatch_after_approval} onChange={e => change({ auto_dispatch_after_approval: e.target.checked })} /></label>
      </div>
    </section>
  </div>;
}

function formatSignedPercent(value: number) {
  const percent = Math.round((Number(value) || 0) * 100);
  return `${percent > 0 ? "+" : ""}${percent}%`;
}

function KnowledgeComposition({ form, change, options }: { form: any; change: (patch: Record<string, unknown>) => void;
  options: Array<{id:string;code:string;name:string;importance:number}> }) {
  const [query, setQuery] = useState("");
  const axes: Array<{knowledge_point_id:string;weight:number;frequency:number;difficulty:number}> = form.new_question_axes || [];
  const selectedIds = new Set(axes.map(item => item.knowledge_point_id));
  const visible = options.filter(item => !selectedIds.has(item.id) && (`${item.code} ${item.name}`).toLowerCase().includes(query.toLowerCase())).slice(0, 30);
  const update = (id: string, patch: Record<string, number>) => change({ new_question_axes: axes.map(item => item.knowledge_point_id === id ? { ...item, ...patch } : item), knowledge_point_ids: axes.map(item => item.knowledge_point_id) });
  const remove = (id: string) => { const next = axes.filter(item => item.knowledge_point_id !== id); change({ new_question_axes: next, knowledge_point_ids: next.map(item => item.knowledge_point_id) }); };
  const add = (id: string) => { if (axes.length >= 10 || selectedIds.has(id)) return; const next = [...axes, { knowledge_point_id: id, weight: axes.length ? .6 : 1, frequency: .5, difficulty: .5 }]; change({ new_question_axes: next, knowledge_point_ids: next.map(item => item.knowledge_point_id) }); setQuery(""); };
  return <div className="knowledge-composition">
    <div className="knowledge-picker-head"><div><b>混合题知识点</b><small>已选择 {axes.length} / 10，第一项默认作为核心知识点</small></div>
      <div className="knowledge-search"><Search /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索知识点名称或编码" /></div></div>
    {query && <div className="knowledge-search-results">{visible.map(item => <button type="button" key={item.id} onClick={() => add(item.id)}><span><b>{item.name}</b><small>{item.code}</small></span><Plus /></button>)}{!visible.length && <span>没有可选知识点</span>}</div>}
    <div className="knowledge-axis-list">{axes.map((axis, index) => { const point = options.find(item => item.id === axis.knowledge_point_id); return <article key={axis.knowledge_point_id}>
      <header><span>{index + 1}</span><div><b>{point?.name || "正在读取知识点"}</b><small>{point?.code || axis.knowledge_point_id}</small></div><button type="button" className="icon-btn" aria-label="移除知识点" onClick={() => remove(axis.knowledge_point_id)}><X /></button></header>
      <div className="knowledge-axis-controls">
        <AxisPresetControl label="命中权重" value={axis.weight} min={0} presets={absoluteWeightPresets} onChange={value => update(axis.knowledge_point_id, { weight: value })} help="该知识点在混合题中的绝对参与度" />
        <AxisPresetControl label="题型频率" value={axis.frequency} min={0} presets={absoluteFrequencyPresets} onChange={value => update(axis.knowledge_point_id, { frequency: value })} help="基于整个题库统计的绝对常见度" />
        <AxisPresetControl label="知识点难度" value={axis.difficulty} min={0} presets={absoluteDifficultyPresets} onChange={value => update(axis.knowledge_point_id, { difficulty: value })} help="该知识点在题目中的绝对难度坐标" />
      </div>
    </article>})}{!axes.length && <div className="composition-empty">搜索并加入 1～10 个知识点后，可分别设置三个绝对向量坐标。</div>}</div>
    <div className="neighbor-expansion"><label><input type="checkbox" checked={!!form.expand_neighbor_knowledge} onChange={event => change({ expand_neighbor_knowledge: event.target.checked })} /><span><b>自动加入临近知识点</b><small>依据知识图谱的相关、先修、易混关系扩展混合题，但总数仍不超过 10 个</small></span></label>
      {form.expand_neighbor_knowledge && <label>最多扩展<select value={form.neighbor_knowledge_limit ?? 2} onChange={event => change({ neighbor_knowledge_limit: Number(event.target.value) })}>{[1,2,3,4,5].map(value => <option key={value} value={value}>{value} 个</option>)}</select></label>}</div>
  </div>;
}

function AxisPresetControl({ label, value, min, presets, onChange, help }: {
  label: string; value: number; min: -1 | 0; presets: Array<{ value: number; label: string }>;
  onChange: (value: number) => void; help: string;
}) {
  const presetValue = presets.some(item => Math.abs(item.value - value) < .001) ? String(value) : "custom";
  const [mode, setMode] = useState(presetValue);
  const percent = Math.round(value * 100);
  return <div className="axis-preset-control">
    <div className="axis-preset-head"><b>{label}</b><strong>{percent > 0 && min < 0 ? "+" : ""}{percent}%</strong></div>
    <select value={mode} onChange={event => {
      const next = event.target.value;
      setMode(next);
      if (next !== "custom") onChange(Number(next));
    }}>
      {presets.map(item => <option key={item.value} value={String(item.value)}>{item.label}（{item.value > 0 && min < 0 ? "+" : ""}{Math.round(item.value * 100)}%）</option>)}
      <option value="custom">自定义微调</option>
    </select>
    {mode === "custom" && <div className="axis-range-row">
      <span>{min * 100}%</span><input aria-label={`${label}百分比`} type="range" min={min * 100} max={100} step={1} value={percent}
        onChange={event => onChange(Number(event.target.value) / 100)} /><span>100%</span>
    </div>}
    <small>{help}</small>
  </div>;
}

function ProfileEditor({ profile, system, campuses, pending, onClose, onSave }: {
  profile?: AutomationProfile; system?: AutomationProfile;
  campuses: Array<{ id: string; name: string }>; pending: boolean; onClose: () => void;
  onSave: (draft: Record<string, unknown>) => void;
}) {
  const base: any = profile?.settings || system?.settings || {
    enabled: false, generation_mode: "class", trigger_days_before: 1, trigger_local_time: "18:00",
    timezone: "Asia/Shanghai", question_count: 5, wrong_question_count: 2, difficulty_offset: 0,
    foundation_offset: 0, frequency_offset: 0, knowledge_strategy: "class_weakness",
    knowledge_point_ids: [], generation_basis: "wrong_regeneration", new_question_axes: [],
    expand_neighbor_knowledge: false, neighbor_knowledge_limit: 2,
    approval_required: true, auto_dispatch_after_approval: true, print_copies: 1,
  };
  const [name, setName] = useState(profile?.name || "日常巩固预设");
  const [description, setDescription] = useState(profile?.description || "用于一组教学班的可复用自动化规则");
  const [campusId, setCampusId] = useState(profile?.campus_id || campuses[0]?.id || "");
  const [form, setForm] = useState<any>({ ...base });
  const profileType = profile?.profile_type || "preset";
  const title = profileType === "system" ? "系统默认设置" : profileType === "campus" ? "校区默认设置" : profile ? "编辑策略预设" : "创建策略预设";
  return <Modal title={title} subtitle="修改后只同步到继承该规则的班级，不覆盖班级例外" onClose={onClose}>
    <div className="profile-identity settings-identity">
      <label>设置名称<input value={name} onChange={e => setName(e.target.value)} /></label>
      {profileType !== "system" && <label>所属校区<select value={campusId} disabled={!!profile?.campus_id} onChange={e => setCampusId(e.target.value)}>
        {campuses.map(campus => <option key={campus.id} value={campus.id}>{campus.name}</option>)}</select></label>}
      <label className="wide">用途说明<input value={description} onChange={e => setDescription(e.target.value)} /></label>
    </div>
    <StrategySettings form={form} setForm={setForm} showEnabled />
    <footer className="modal-actions"><button className="quiet" onClick={onClose}>取消</button><button className="primary"
      disabled={pending || name.trim().length < 2 || !campusId && profileType !== "system" || form.wrong_question_count > form.question_count}
      onClick={() => onSave({ profile_type: profileType, campus_id: profileType === "system" ? null : campusId,
        name: name.trim(), description: description.trim(), settings: form })}>{pending ? "保存中" : "保存并同步"}</button></footer>
  </Modal>;
}

function ProfileAssignment({ profile, policies, pending, onClose, onSave }: {
  profile: AutomationProfile; policies: AutomationPolicy[]; pending: boolean; onClose: () => void; onSave: (ids: string[]) => void;
}) {
  const available = policies.filter(item => !profile.campus_id || item.campus_id === profile.campus_id);
  const [selected, setSelected] = useState<string[]>([]);
  const toggle = (id: string) => setSelected(current => current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  return <Modal title={`应用预设：${profile.name}`} subtitle="选择需要统一采用此预设的教学班；原班级例外将被替换" onClose={onClose}>
    <div className="assignment-toolbar"><button className="quiet small" onClick={() => setSelected(available.map(x => x.schedule_slot_id))}>全选</button>
      <button className="quiet small" onClick={() => setSelected([])}>清空</button><span>已选 {selected.length} / {available.length}</span></div>
    <div className="assignment-list">{available.map(item => <label key={item.id}><input type="checkbox" checked={selected.includes(item.schedule_slot_id)} onChange={() => toggle(item.schedule_slot_id)} />
      <span><b>{item.class_name} · {item.course_name}</b><small>{item.campus_name} · {weekdayLabel[item.weekday]} {item.start_time} · {item.teacher_name}</small></span>
      <em>{item.inheritance_mode === "custom" ? "当前为班级例外" : item.profile_name || "系统默认"}</em></label>)}</div>
    <footer className="modal-actions"><button className="quiet" onClick={onClose}>取消</button><button className="primary" disabled={pending || !selected.length} onClick={() => onSave(selected)}>{pending ? "应用中" : `应用到 ${selected.length} 个班`}</button></footer>
  </Modal>;
}

function Home({
  summary,
  token,
  go,
}: {
  summary?: Summary;
  token: string;
  go: (p: Page) => void;
}) {
  const items = [
    {
      label: "正式题目",
      value: summary?.active_questions ?? 0,
      note: `${summary?.pending_questions ?? 0} 道等待审校`,
      go: "questions" as Page,
    },
    {
      label: "课程知识点",
      value: summary?.knowledge_points ?? 0,
      note: "按教材版本管理",
      go: "knowledge" as Page,
    },
    {
      label: "今日小测",
      value: summary?.papers_today ?? 0,
      note: `${summary?.print_batches_today ?? 0} 个打印批次`,
      go: "assessments" as Page,
    },
    {
      label: "批阅待确认",
      value: summary?.grading_review ?? 0,
      note: `今日回流 ${summary?.grading_today ?? 0}`,
      go: "grading" as Page,
    },
  ];
  return (
    <>
      <section className="welcome">
        <div>
          <p>
            {new Date().toLocaleDateString("zh-CN", {
              month: "long",
              day: "numeric",
              weekday: "long",
            })}
          </p>
          <h2>今天的教学工作</h2>
          <span>从小测准备、打印到批阅和学情反馈，按真实任务顺序处理。</span>
        </div>
        <button className="primary" onClick={() => go("assessments")}>
          <Plus />
          创建今日小测
        </button>
      </section>
      <div className="metric-grid">
        {items.map((x) => (
          <button key={x.label} onClick={() => go(x.go)}>
            <span>{x.label}</span>
            <strong>{x.value}</strong>
            <small>{x.note}</small>
            <ChevronRight />
          </button>
        ))}
      </div>
      <div className="two-col">
        <section className="paper-panel">
          <header>
            <div>
              <h3>今日处理顺序</h3>
              <p>系统只突出需要教师决定的事项</p>
            </div>
          </header>
          <div className="task-list">
            <button onClick={() => go("questions")}>
              <span className="task-icon amber">1</span>
              <div>
                <b>审校新题</b>
                <small>
                  {summary?.pending_questions || 0}{" "}
                  道题等待确认知识点、答案和难度
                </small>
              </div>
              <ChevronRight />
            </button>
            <button onClick={() => go("grading")}>
              <span className="task-icon red">2</span>
              <div>
                <b>复核批阅异常</b>
                <small>
                  {summary?.grading_review || 0}{" "}
                  份答题卡存在低置信度或学号未匹配
                </small>
              </div>
              <ChevronRight />
            </button>
            <button onClick={() => go("automation")}>
              <span className="task-icon green">3</span>
              <div>
                <b>审校教学建议</b>
                <small>
                  {summary?.pending_approvals || 0} 项分类或学情建议等待审批
                </small>
              </div>
              <ChevronRight />
            </button>
          </div>
        </section>
        <section className="paper-panel teaching-loop">
          <header>
            <div>
              <h3>本期教学闭环</h3>
              <p>每一步都有来源和去向</p>
            </div>
          </header>
          <ol>
            <li>
              <b>备课</b>
              <span>知识点与题库</span>
            </li>
            <li>
              <b>测评</b>
              <span>组卷与答题卡</span>
            </li>
            <li>
              <b>批阅</b>
              <span>自动识别与复核</span>
            </li>
            <li>
              <b>反馈</b>
              <span>学生学情与次日计划</span>
            </li>
          </ol>
        </section>
      </div>
      {!token && (
        <div className="permission-note">
          <LogIn />
          <div>
            <b>连接校区权限后可开始操作</b>
            <span>公开总览不包含题目正文和学生数据。</span>
          </div>
        </div>
      )}
    </>
  );
}

function ClassWorkspace({
  token,
  allCampuses,
  go,
}: {
  token: string;
  allCampuses: boolean;
  go: (page: Page, classId?: string, studentId?: string) => void;
}) {
  const [term, setTerm] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedClass, setSelectedClass] = useState("");
  const pageSize = 18;
  const classes = useQuery({
    queryKey: ["teaching-class-workspace", term, offset, token, allCampuses],
    enabled: !!token,
    queryFn: () => api<{ items: TeachingClassWorkspace[]; total: number; limit: number; offset: number }>(
      `/api/teaching-engine/workspace/classes?limit=${pageSize}&offset=${offset}${term.trim() ? `&query=${encodeURIComponent(term.trim())}` : ""}${allCampuses ? "&scope=all" : ""}`,
      { token },
    ),
  });
  useEffect(() => setOffset(0), [term]);
  if (selectedClass) return <ClassDetailPage classId={selectedClass} token={token} allCampuses={allCampuses} go={go} onBack={() => setSelectedClass("")} />;
  return <>
    <div className="page-head">
      <div>
        <h2>班级教学</h2>
        <p>按班级集中查看学生、小测、待批阅和薄弱学情，不在多个模块之间反复查找。</p>
      </div>
      <label className="class-search"><Search /><input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="搜索班级、课程或教师" /></label>
    </div>
    {!token ? <div className="permission-note"><LogIn /><div><b>请先连接校区账号</b><span>班级、学生与批阅信息只对所属校区开放。</span></div></div> :
      classes.isLoading ? <section className="table-panel"><Empty>正在读取班级教学状态</Empty></section> :
      classes.isError ? <section className="table-panel"><p className="form-error">{(classes.error as Error).message}</p></section> :
      classes.data?.items.length ? <>
        <div className="class-workspace-grid">
          {classes.data.items.map((item) => {
            const attention = item.grading_review + item.pending_publish;
            const ratio = item.capacity > 0 ? Math.min(100, Math.round(item.active_students / item.capacity * 100)) : 0;
            return <article className="class-workspace-card" key={item.id}>
              <header>
                <div><small>{item.campus_name} · {item.grade || "未设年级"} {item.subject || ""}</small><h3>{item.name}</h3><p>{item.course_name}</p></div>
                {attention ? <span className="attention-count">{attention} 项待办</span> : <span className="settled">今日清晰</span>}
              </header>
              <div className="class-meta"><span>任课教师<b>{item.teacher_name}</b></span><span>在班人数<b>{item.active_students}/{item.capacity}</b></span></div>
              <div className="capacity-line"><i style={{ width: `${ratio}%` }} /></div>
              <div className="class-teaching-metrics">
                <span><b>{item.pending_publish}</b>待发布</span>
                <span><b>{item.grading_review}</b>待复核</span>
                <span><b>{item.waiting_grading}</b>待结卷</span>
                <span><b>{item.weak_students}</b>需关注学生</span>
              </div>
              <div className="class-performance">
                <span>近 30 天作答 <b>{item.attempts_30d}</b></span>
                <span>正确率 <b>{item.accuracy_30d == null ? "—" : `${Math.round(item.accuracy_30d * 100)}%`}</b></span>
              </div>
              <div className="latest-class-paper">
                <span>最近试卷</span>
                {item.latest_paper_title ? <><b>{item.latest_paper_title}</b><small>{fmtDate(item.latest_paper_at)} · {statusLabel[item.latest_paper_status || ""] || item.latest_paper_status}</small></> : <b>尚未组卷</b>}
              </div>
              <footer>
                <button className="quiet small" onClick={() => go("assessments", item.id)}>试卷与打印</button>
                <button className="quiet small" onClick={() => go("grading", item.id)}>批阅回流</button>
                <button className="primary small" onClick={() => setSelectedClass(item.id)}>打开班级<ChevronRight /></button>
              </footer>
            </article>;
          })}
        </div>
        <div className="pager"><span>共 {classes.data.total} 个教学班</span><button className="quiet small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>上一页</button><button className="quiet small" disabled={offset + pageSize >= classes.data.total} onClick={() => setOffset(offset + pageSize)}>下一页</button></div>
      </> : <section className="table-panel"><Empty>{term ? "没有匹配的教学班" : "当前校区尚未建立教学班"}</Empty></section>}
  </>;
}

const learningLayerLabel: Record<string, string> = {
  no_evidence: "暂无证据",
  support: "需要辅导",
  steady: "稳步学习",
  advanced: "进阶学习",
};
const planStatusLabel: Record<string, string> = {
  draft: "草稿", active: "执行中", completed: "已完成", cancelled: "已取消",
  pending: "待开始", in_progress: "教学中", skipped: "已跳过",
};
const planEventLabel: Record<string, string> = {
  created: "创建计划", updated: "修改计划", activate: "启用计划",
  complete: "完成计划", cancel: "取消计划", item_status: "更新知识点进度",
};
function PlanStatus({ value }: { value: string }) {
  return <span className={`status status-${value}`}>{planStatusLabel[value] || value}</span>;
}

function ClassDetailPage({ classId, token, allCampuses, go, onBack }: {
  classId: string; token: string; allCampuses: boolean; go: (page: Page, classId?: string, studentId?: string) => void; onBack: () => void;
}) {
  const qc = useQueryClient();
  const [createPlan, setCreatePlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const detail = useQuery({
    queryKey: ["teaching-class-detail", classId, token, allCampuses], enabled: !!token,
    queryFn: () => api<TeachingClassDetail>(`/api/teaching-engine/workspace/classes/${classId}${allCampuses ? "?scope=all" : ""}`, { token }),
  });
  if (detail.isLoading) return <section className="table-panel"><Empty>正在读取班级教学档案</Empty></section>;
  if (detail.isError || !detail.data) return <section className="table-panel"><button className="text-action" onClick={onBack}>返回班级列表</button><p className="form-error">{(detail.error as Error)?.message || "班级教学档案读取失败"}</p></section>;
  const data = detail.data;
  const activePlan = data.plans.find((plan) => plan.status === "active");
  return <>
    <div className="class-detail-head">
      <button className="quiet small" onClick={onBack}>← 返回班级列表</button>
      <div><small>{data.class.campus_name} · {data.class.grade} {data.class.subject}</small><h2>{data.class.name}</h2><p>{data.class.course_name} · 任课教师 {data.class.teacher_name}</p></div>
      <div className="class-detail-actions"><button className="quiet" onClick={() => go("grading", classId)}>查看批阅</button><button className="primary" onClick={() => go("assessments", classId)}><Plus />创建小测</button></div>
    </div>
    <div className="class-summary-row">
      <span><b>{data.summary.students}</b>在班学生</span><span><b>{data.summary.support}</b>需要辅导</span>
      <span><b>{data.summary.advanced}</b>进阶学习</span><span><b>{data.summary.no_evidence}</b>暂无作答证据</span>
      <span><b>{data.class.completed_lessons || 0}/{data.class.planned_lessons || 0}</b>课程进度</span>
    </div>
    <section className="class-plan-panel">
      <header><div><h3>班级教学计划</h3><p>计划必须绑定规范知识点；同一班级同时只能启用一份计划。</p></div><button className="primary small" onClick={() => setCreatePlan(true)}><Plus />新建计划</button></header>
      {activePlan && <div className="active-plan-strip"><span>当前计划</span><b>{activePlan.title}</b><em>{activePlan.resolved_items}/{activePlan.item_count} 个知识点已处理</em><button className="text-action" onClick={() => setSelectedPlan(activePlan.id)}>继续执行</button></div>}
      <div className="plan-list">{data.plans.length ? data.plans.map((plan) => <button key={plan.id} onClick={() => setSelectedPlan(plan.id)}>
        <div><b>{plan.title}</b><small>{plan.scope_name} · {plan.period_start} 至 {plan.period_end}</small></div><PlanStatus value={plan.status} />
        <span>{plan.resolved_items}/{plan.item_count} 知识点</span><ChevronRight /></button>) : <Empty>尚未建立班级教学计划</Empty>}</div>
    </section>
    <div className="class-detail-columns">
      <section className="table-panel class-roster"><header><div><h3>学生学习分层</h3><p>分层由作答正确率和知识点掌握证据计算，不是人工标签。</p></div></header>
        {data.roster.length ? <table><thead><tr><th>学生</th><th>证据</th><th>正确率</th><th>掌握度</th><th>薄弱点</th><th>学习层</th><th>操作</th></tr></thead><tbody>
          {data.roster.map((student) => <tr key={student.id}><td><b>{student.name}</b><small>{student.external_student_no || "无学号"}</small></td>
            <td>{student.attempts} 次</td><td>{student.accuracy == null ? "—" : `${Math.round(student.accuracy * 100)}%`}</td>
            <td>{student.mastery_avg == null ? "—" : `${Math.round(student.mastery_avg * 100)}%`}</td><td>{student.weak_points}</td>
            <td><span className={`learning-layer layer-${student.learning_layer}`}>{learningLayerLabel[student.learning_layer]}</span></td>
            <td><button className="text-action" onClick={() => go("learning", "", student.id)}>查看学情</button></td></tr>)}</tbody></table> : <Empty>当前班级尚无在读学生</Empty>}
      </section>
      <section className="table-panel class-knowledge"><header><div><h3>班级知识点证据</h3><p>最近 90 天，优先显示低正确率知识点。</p></div></header>
        {data.knowledge.length ? <div>{data.knowledge.map((point) => <article key={point.id}><div><b>{point.name}</b><small>{point.code} · {point.student_count} 名学生</small></div>
          <strong>{point.accuracy == null ? "—" : `${Math.round(point.accuracy * 100)}%`}</strong><i><em style={{width:`${Math.round((point.accuracy || 0) * 100)}%`}} /></i><span>{point.attempts} 次作答</span></article>)}</div> : <Empty>尚无班级知识点作答证据</Empty>}
      </section>
    </div>
    {createPlan && <TeachingPlanDialog classId={classId} token={token} scopes={data.available_scopes} onClose={() => setCreatePlan(false)} onDone={() => {
      setCreatePlan(false); qc.invalidateQueries({ queryKey: ["teaching-class-detail", classId] });
    }} />}
    {selectedPlan && <TeachingPlanDetailDialog planId={selectedPlan} token={token} scopes={data.available_scopes} onClose={() => setSelectedPlan("")} onChanged={() => {
      qc.invalidateQueries({ queryKey: ["teaching-class-detail", classId] }); qc.invalidateQueries({ queryKey: ["teaching-plan-detail", selectedPlan] });
    }} />}
  </>;
}

function TeachingPlanDialog({ classId, token, scopes, existing, onClose, onDone }: {
  classId: string; token: string; scopes: TeachingClassDetail["available_scopes"]; existing?: TeachingPlanDetail; onClose: () => void; onDone: () => void;
}) {
  const initialScope = existing?.plan.scope_id || scopes[0]?.id || "";
  const [scopeId, setScopeId] = useState(initialScope);
  const [items, setItems] = useState<Record<string, { target_mastery: number; planned_sessions: number }>>(() => Object.fromEntries(
    (existing?.items || []).map((item) => [item.knowledge_point_id, { target_mastery: item.target_mastery, planned_sessions: item.planned_sessions }]),
  ));
  const [error, setError] = useState("");
  const knowledge = useQuery({
    queryKey: ["plan-knowledge", scopeId, token], enabled: !!scopeId,
    queryFn: () => api<{ items: Knowledge[] }>(`/api/teaching-engine/knowledge-points?scope_id=${scopeId}`, { token }),
  });
  const endDefault = new Date(); endDefault.setDate(endDefault.getDate() + 30);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    const values = Object.fromEntries(new FormData(event.currentTarget));
    if (!Object.keys(items).length) return setError("至少选择一个教学知识点");
    const body = { scope_id: scopeId, title: values.title, objective: values.objective,
      period_start: values.period_start, period_end: values.period_end,
      items: Object.entries(items).map(([knowledge_point_id, value]) => ({ knowledge_point_id, ...value })) };
    try {
      if (existing) await api(`/api/teaching-engine/workspace/plans/${existing.plan.id}`, { method: "PUT", token,
        body: JSON.stringify({ ...body, expected_version: existing.plan.version_no }) });
      else await post(`/api/teaching-engine/workspace/classes/${classId}/plans`, body, token);
      onDone();
    } catch (reason) { setError((reason as Error).message); }
  }
  return <Modal title={existing ? "修改教学计划" : "新建教学计划"} subtitle="选择本阶段真正要完成的知识点、目标掌握度和预计课次" onClose={onClose}>
    <form className="form teaching-plan-form" onSubmit={submit}><div className="form-grid">
      <label>课程范围<select value={scopeId} onChange={(e) => { setScopeId(e.target.value); setItems({}); }} required>{scopes.map((scope) => <option key={scope.id} value={scope.id}>{scope.grade} · {scope.subject} · {scope.name}</option>)}</select></label>
      <label>计划名称<input name="title" required defaultValue={existing?.plan.title || ""} placeholder="例如：一次函数阶段教学计划" /></label>
      <label>开始日期<input name="period_start" type="date" required defaultValue={existing?.plan.period_start || new Date().toISOString().slice(0,10)} /></label>
      <label>结束日期<input name="period_end" type="date" required defaultValue={existing?.plan.period_end || endDefault.toISOString().slice(0,10)} /></label>
      <label className="wide">教学目标<textarea name="objective" rows={3} defaultValue={existing?.plan.objective || ""} placeholder="说明本阶段学生应达到的可验证结果" /></label>
      <fieldset className="wide plan-point-picker"><legend>知识点与目标</legend>{knowledge.isLoading ? <span className="muted">正在读取知识点…</span> : knowledge.data?.items.filter((point) => point.depth > 0).map((point) => {
        const selected = items[point.id]; return <div key={point.id} className={selected ? "selected" : ""}><label><input type="checkbox" checked={!!selected} onChange={(e) => setItems((current) => {
          const next = {...current}; if (e.target.checked) next[point.id] = {target_mastery:.8,planned_sessions:1}; else delete next[point.id]; return next;
        })} /><span><b>{point.name}</b><small>{point.code}</small></span></label>{selected && <><label>目标<input type="number" min="0.1" max="1" step="0.05" value={selected.target_mastery} onChange={(e) => setItems((current) => ({...current,[point.id]:{...current[point.id],target_mastery:Number(e.target.value)}}))} /></label>
          <label>课次<input type="number" min="1" max="200" value={selected.planned_sessions} onChange={(e) => setItems((current) => ({...current,[point.id]:{...current[point.id],planned_sessions:Number(e.target.value)}}))} /></label></>}</div>;
      })}</fieldset>
    </div>{error && <p className="form-error">{error}</p>}<footer><button className="quiet" type="button" onClick={onClose}>取消</button><button className="primary">{existing ? "保存修改" : "保存草稿"}</button></footer></form>
  </Modal>;
}

function TeachingPlanDetailDialog({ planId, token, scopes, onClose, onChanged }: {
  planId: string; token: string; scopes: TeachingClassDetail["available_scopes"]; onClose: () => void; onChanged: () => void;
}) {
  const [edit, setEdit] = useState(false);
  const [makeQuiz, setMakeQuiz] = useState(false);
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState("");
  const detail = useQuery({ queryKey: ["teaching-plan-detail", planId, token],
    queryFn: () => api<TeachingPlanDetail>(`/api/teaching-engine/workspace/plans/${planId}`, { token }) });
  const lifecycle = useMutation({ mutationFn: (action: "activate"|"complete"|"cancel") => post(`/api/teaching-engine/workspace/plans/${planId}/lifecycle`, { action, reason }, token),
    onMutate: () => setActionError(""), onSuccess: onChanged, onError: (error) => setActionError((error as Error).message) });
  const itemStatus = useMutation({ mutationFn: ({id,status}:{id:string;status:string}) => post(`/api/teaching-engine/workspace/plans/${planId}/items/${id}/status`, {status,evidence_note:reason}, token),
    onMutate: () => setActionError(""), onSuccess: onChanged, onError: (error) => setActionError((error as Error).message) });
  if (edit && detail.data) return <TeachingPlanDialog classId={detail.data.plan.class_group_id} token={token} scopes={scopes} existing={detail.data} onClose={() => setEdit(false)} onDone={() => {setEdit(false);onChanged();}} />;
  if (makeQuiz && detail.data) return <AssessmentDialog scopes={scopes} classes={[{id:detail.data.plan.class_group_id,name:detail.data.plan.class_name}]}
    token={token} initialClassId={detail.data.plan.class_group_id} initialScopeId={detail.data.plan.scope_id}
    initialKnowledgeIds={detail.data.items.filter((item) => !["completed","skipped"].includes(item.status)).map((item) => item.knowledge_point_id)}
    initialTitle={`${detail.data.plan.title}课堂小测`} onClose={() => setMakeQuiz(false)} onDone={() => {setMakeQuiz(false);onChanged();}} />;
  return <Modal title={detail.data?.plan.title || "教学计划"} subtitle={detail.data ? `${detail.data.plan.scope_name} · v${detail.data.plan.version_no}` : "正在读取"} onClose={onClose}>
    {detail.isLoading ? <Empty>正在读取教学计划</Empty> : detail.isError || !detail.data ? <p className="form-error">{(detail.error as Error)?.message}</p> : <div className="plan-detail">
      <div className="plan-detail-summary"><PlanStatus value={detail.data.plan.status} /><span>{detail.data.plan.period_start} 至 {detail.data.plan.period_end}</span><p>{detail.data.plan.objective || "未填写教学目标"}</p></div>
      <div className="plan-item-list">{detail.data.items.map((item) => <article key={item.id}><span>{item.sequence_no}</span><div><b>{item.name}</b><small>{item.code} · 目标 {Math.round(item.target_mastery*100)}% · {item.planned_sessions} 课次</small>
        <small>{item.attempts ? `${item.student_count} 名学生 · ${item.attempts} 次作答 · 正确率 ${Math.round((item.accuracy || 0)*100)}%${item.mastery_avg == null ? "" : ` · 平均掌握 ${Math.round(item.mastery_avg*100)}%`}` : "本计划期内尚无作答证据"}</small></div><PlanStatus value={item.status} />
        {detail.data!.plan.status === "active" && item.status === "pending" && <button className="quiet small" onClick={() => itemStatus.mutate({id:item.id,status:"in_progress"})}>开始</button>}
        {detail.data!.plan.status === "active" && item.status === "in_progress" && <button className="primary small" onClick={() => itemStatus.mutate({id:item.id,status:"completed"})}>完成</button>}
        {detail.data!.plan.status === "active" && ["pending","in_progress"].includes(item.status) && <button className="text-action" onClick={() => itemStatus.mutate({id:item.id,status:"skipped"})}>跳过</button>}</article>)}</div>
      <label className="approval-comment">本次操作说明<textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="记录完成依据；取消计划时必须填写原因" /></label>
      {actionError && <p className="form-error">{actionError}</p>}
      <div className="plan-event-list"><h4>操作记录</h4>{detail.data.events.map((event) => <article key={event.id}><b>{event.actor_name}</b><span>{planEventLabel[event.event_type] || event.event_type} · {fmtDate(event.created_at)}</span>{event.reason && <p>{event.reason}</p>}</article>)}</div>
      <footer className="modal-actions"><button className="quiet" onClick={() => setMakeQuiz(true)}>按计划创建小测</button>{detail.data.plan.status === "draft" && <><button className="quiet" onClick={() => setEdit(true)}>修改</button><button className="primary" onClick={() => lifecycle.mutate("activate")}>启用计划</button></>}
        {detail.data.plan.status === "active" && <button className="primary" onClick={() => lifecycle.mutate("complete")}>完成计划</button>}
        {["draft","active"].includes(detail.data.plan.status) && <button className="quiet danger" disabled={reason.trim().length<2} onClick={() => lifecycle.mutate("cancel")}>取消计划</button>}</footer>
    </div>}
  </Modal>;
}

function KnowledgePage({ scopes, token }: { scopes: Scope[]; token: string }) {
  const [selected, setSelected] = useState("");
  const knowledge = useQuery({
    queryKey: ["knowledge", selected, token],
    enabled: !!selected && !!token,
    queryFn: () =>
      api<{ items: Knowledge[] }>(
        `/api/teaching-engine/knowledge-points?scope_id=${selected}`,
        { token },
      ),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h2>课程与知识点</h2>
          <p>
            先确定教材范围，再维护知识点树；题目和学情都沿用同一套课程坐标。
          </p>
        </div>
      </div>
      <div className="knowledge-layout">
        <section className="scope-list">
          <header>
            <b>课程范围</b>
            <span>{scopes.length}</span>
          </header>
          {scopes.length ? (
            scopes.map((s) => (
              <button
                key={s.id}
                className={selected === s.id ? "selected" : ""}
                onClick={() => setSelected(s.id)}
              >
                <div>
                  <b>
                    {s.grade} · {s.subject}
                  </b>
                  <span>{s.name}</span>
                </div>
                <small>{s.knowledge_count} 个知识点</small>
              </button>
            ))
          ) : (
            <Empty>尚未建立课程范围</Empty>
          )}
        </section>
        <section className="paper-panel knowledge-tree">
          <header>
            <div>
              <h3>
                {selected
                  ? scopes.find((s) => s.id === selected)?.name
                  : "选择课程范围"}
              </h3>
              <p>知识点按教学顺序和上下级关系展示</p>
            </div>
          </header>
          {!selected ? (
            <Empty>从左侧选择一门课程</Empty>
          ) : knowledge.data?.items.length ? (
            <div>
              {knowledge.data.items.map((k) => (
                <article key={k.id} style={{ marginLeft: k.depth * 22 }}>
                  <span className="node-dot" />
                  <div>
                    <b>{k.name}</b>
                    <small>
                      {k.code} · 已关联 {k.question_count} 题
                    </small>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <Empty>该课程还没有知识点</Empty>
          )}
        </section>
      </div>
    </>
  );
}

function ImportPage({
  scopes,
  token,
  notify,
}: {
  scopes: Scope[];
  token: string;
  notify: (s: string) => void;
}) {
  const qc = useQueryClient();
  const [sourceId, setSourceId] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [filename, setFilename] = useState("");
  const [payload, setPayload] = useState("");
  const [sourceDialog, setSourceDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("");
  const sources = useQuery({
    queryKey: ["question-sources", token],
    enabled: !!token,
    queryFn: () => api<{ items: QuestionSource[] }>("/api/teaching-engine/question-sources", { token }),
  });
  const batches = useQuery({
    queryKey: ["question-imports", token],
    enabled: !!token,
    queryFn: () => api<{ items: ImportBatch[] }>("/api/teaching-engine/question-imports", { token }),
  });
  const batchDetail = useQuery({
    queryKey: ["question-import-detail", selectedBatch, token],
    enabled: !!token && !!selectedBatch,
    queryFn: () => api<{
      batch: ImportBatch;
      items: Array<{ row_no: number; validation_status: string; errors: string[]; normalized_payload?: { stem?: { text?: string } } }>;
    }>(`/api/teaching-engine/question-imports/${selectedBatch}`, { token }),
  });
  const validate = useMutation({
    mutationFn: async () => {
      let items: unknown;
      try {
        items = JSON.parse(payload);
      } catch {
        throw new Error("JSON 格式不正确，请检查括号和引号");
      }
      if (!Array.isArray(items)) throw new Error("导入内容必须是题目数组");
      return post<{ id: string; status: string; valid_count: number; invalid_count: number; duplicate_count: number }>(
        "/api/teaching-engine/question-imports/validate",
        {
          source_id: sourceId,
          scope_id: scopeId,
          idempotency_key: crypto.randomUUID(),
          input_format: "json",
          original_filename: filename || "网页粘贴.json",
          items,
        },
        token,
      );
    },
    onSuccess: (d) => {
      notify(`校验完成：有效 ${d.valid_count}，错误 ${d.invalid_count}，重复 ${d.duplicate_count}`);
      qc.invalidateQueries({ queryKey: ["question-imports"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  const commit = useMutation({
    mutationFn: (id: string) => post<{ imported_count: number }>(`/api/teaching-engine/question-imports/${id}/commit`, {}, token),
    onSuccess: (d) => {
      notify(`已导入 ${d.imported_count} 道题，全部进入待审校区`);
      qc.invalidateQueries({ queryKey: ["question-imports"] });
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  const reviewSource = useMutation({
    mutationFn: (id: string) => post(`/api/teaching-engine/question-sources/${id}/review`, {
      decision: "verified",
      commercial_use_allowed: true,
      comment: "管理员已核对授权文件与商用范围",
    }, token),
    onSuccess: () => {
      notify("题源授权状态已核验");
      qc.invalidateQueries({ queryKey: ["question-sources"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  const cancelBatch = useMutation({
    mutationFn: (id: string) => post(`/api/teaching-engine/question-imports/${id}/cancel`, {}, token),
    onSuccess: () => {
      notify("导入批次已取消，审计记录已保留");
      setSelectedBatch("");
      qc.invalidateQueries({ queryKey: ["question-imports"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  const sample = JSON.stringify([{
    question_type: "single",
    stem: { text: "请填写题干" },
    options: ["A. 选项一", "B. 选项二"],
    answer: { value: "A" },
    difficulty: 0.5,
    max_score: 2,
    tags: ["单元检测"],
    knowledge_points: [],
  }], null, 2);
  return (
    <>
      <div className="page-head">
        <div>
          <h2>题库导入</h2>
          <p>先核验题源授权，再逐题校验、查重和人工审校；导入不等于发布。</p>
        </div>
        <button className="quiet" onClick={() => setSourceDialog(true)} disabled={!token}>
          <Plus />登记题源
        </button>
      </div>
      <div className="import-layout">
        <section className="paper-panel import-form">
          <header><div><h3>新建导入批次</h3><p>当前支持规范 JSON；单批最多 500 题</p></div></header>
          <div className="form">
            <div className="form-grid">
              <label>已授权题源
                <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                  <option value="">请选择</option>
                  {sources.data?.items.filter((x) => x.status === "verified").map((x) =>
                    <option key={x.id} value={x.id}>{x.name} · {x.provider}</option>)}
                </select>
              </label>
              <label>课程范围
                <select value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
                  <option value="">请选择</option>
                  {scopes.map((s) => <option key={s.id} value={s.id}>{s.grade} · {s.subject} · {s.name}</option>)}
                </select>
              </label>
            </div>
            <label className="file-pick">选择 JSON 文件
              <input type="file" accept="application/json,.json" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setFilename(file.name);
                setPayload(await file.text());
              }} />
              <span>{filename || "也可以直接在下方粘贴"}</span>
            </label>
            <label>题目数据
              <textarea className="code-input" rows={16} value={payload} onChange={(e) => setPayload(e.target.value)} placeholder={sample} />
            </label>
            <div className="form-actions">
              <button className="quiet" type="button" onClick={() => setPayload(sample)}>载入格式示例</button>
              <button className="primary" disabled={!sourceId || !scopeId || !payload || validate.isPending}
                onClick={() => validate.mutate()}><ShieldCheck />{validate.isPending ? "校验中" : "校验并建立批次"}</button>
            </div>
          </div>
        </section>
        <aside className="paper-panel source-register">
          <header><div><h3>题源授权</h3><p>只有已核验、可商用题源能够入库</p></div></header>
          <div className="source-list">
            {sources.data?.items.length ? sources.data.items.map((source) => (
              <article key={source.id}>
                <div><b>{source.name}</b><small>{source.provider} · {source.license_type}</small></div>
                <Status value={source.status} />
                {source.status === "draft" && ["internal", "commercial", "open"].includes(source.license_type) && (
                  <button className="text-action" onClick={() => {
                    if (window.confirm("请确认已经阅读授权文件，并确认允许本系统商业使用。")) reviewSource.mutate(source.id);
                  }}>核验授权</button>
                )}
              </article>
            )) : <Empty>尚未登记题源</Empty>}
          </div>
        </aside>
      </div>
      <section className="table-panel import-table">
        <table>
          <thead><tr><th>批次</th><th>题源与范围</th><th>校验结果</th><th>状态</th><th>创建时间</th><th>操作</th></tr></thead>
          <tbody>
            {batches.data?.items.map((batch) => (
              <tr key={batch.id}>
                <td><b>{batch.original_filename || "未命名批次"}</b><small>{batch.id.slice(0, 8)}</small></td>
                <td><b>{batch.source_name}</b><small>{batch.grade} · {batch.subject} · {batch.scope_name}</small></td>
                <td><span className="count-ok">有效 {batch.valid_count}</span> · <span className="count-bad">错误 {batch.invalid_count}</span> · 重复 {batch.duplicate_count}</td>
                <td><Status value={batch.status} /><small>{batch.validation_summary?.message}</small></td>
                <td>{fmtDate(batch.created_at)}</td>
                <td>
                  <button className="text-action" onClick={() => setSelectedBatch(batch.id)}>查看校验</button>
                  {batch.status === "ready" && <button className="text-action" onClick={() => commit.mutate(batch.id)}>确认入库</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!batches.data?.items.length && <Empty>还没有导入批次</Empty>}
      </section>
      {sourceDialog && <SourceDialog token={token} onClose={() => setSourceDialog(false)} onDone={() => {
        setSourceDialog(false); notify("题源已登记，请完成授权核验"); qc.invalidateQueries({ queryKey: ["question-sources"] });
      }} />}
      {selectedBatch && <Modal title="导入校验明细" subtitle="错误行不会进入题库；修正原文件后请建立新批次" onClose={() => setSelectedBatch("")}>
        <div className="import-detail-summary">
          <span>总数 <b>{batchDetail.data?.batch.total_count ?? "—"}</b></span>
          <span>有效 <b>{batchDetail.data?.batch.valid_count ?? "—"}</b></span>
          <span>错误 <b>{batchDetail.data?.batch.invalid_count ?? "—"}</b></span>
          <span>重复 <b>{batchDetail.data?.batch.duplicate_count ?? "—"}</b></span>
        </div>
        <div className="import-detail-list">
          {batchDetail.data?.items.map((item) => <article key={item.row_no}>
            <span>第 {item.row_no} 题</span>
            <Status value={item.validation_status} />
            <b>{item.normalized_payload?.stem?.text || "未识别题干"}</b>
            <small>{item.errors?.length ? item.errors.join("；") : "格式校验通过"}</small>
          </article>)}
        </div>
        <div className="form-actions">
          {batchDetail.data?.batch.status && ["review", "ready", "failed"].includes(batchDetail.data.batch.status) &&
            <button className="quiet danger" onClick={() => cancelBatch.mutate(selectedBatch)}>取消批次</button>}
          {batchDetail.data?.batch.status === "ready" && <button className="primary" onClick={() => commit.mutate(selectedBatch)}>确认入库</button>}
          <button className="quiet" onClick={() => setSelectedBatch("")}>关闭</button>
        </div>
      </Modal>}
    </>
  );
}

function SourceDialog({ token, onClose, onDone }: { token: string; onClose: () => void; onDone: () => void }) {
  const [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await post("/api/teaching-engine/question-sources", {
        name: f.get("name"), provider: f.get("provider"), source_url: f.get("source_url") || null,
        license_type: f.get("license_type"), commercial_use_allowed: false,
        attribution_required: f.get("attribution_required") === "on",
        attribution_text: f.get("attribution_text") || null,
        license_url: f.get("license_url") || null, terms_text: f.get("terms_text") || null,
      }, token);
      onDone();
    } catch (err) { setError((err as Error).message); }
  }
  return <Modal title="登记题源" subtitle="登记后仍需管理员核对授权文件" onClose={onClose}>
    <form className="form" onSubmit={submit}>
      <div className="form-grid">
        <label>题源名称<input name="name" required /></label>
        <label>提供方<input name="provider" required /></label>
        <label>许可类型<select name="license_type" required><option value="internal">内部自有</option><option value="commercial">商业授权</option><option value="open">开放许可</option><option value="noncommercial">仅非商业</option><option value="restricted">受限</option><option value="unknown">待确认</option></select></label>
        <label>题源网址<input name="source_url" type="url" /></label>
        <label>许可网址<input name="license_url" type="url" /></label>
        <label className="check-label"><input name="attribution_required" type="checkbox" />需要署名</label>
      </div>
      <label>署名文字<input name="attribution_text" /></label>
      <label>授权条款摘要<textarea name="terms_text" rows={5} placeholder="粘贴关键授权条款，系统只保存不可逆摘要用于审计" /></label>
      {error && <p className="error-text">{error}</p>}
      <div className="form-actions"><button type="button" className="quiet" onClick={onClose}>取消</button><button className="primary">保存题源</button></div>
    </form>
  </Modal>;
}

function QuestionPage({
  scopes,
  token,
  notify,
}: {
  scopes: Scope[];
  token: string;
  notify: (s: string) => void;
}) {
  const qc = useQueryClient();
  const [scope, setScope] = useState("");
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [create, setCreate] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [reviewQuestion, setReviewQuestion] = useState<Question | null>(null);
  const [reviewComment, setReviewComment] = useState("");
  const questions = useQuery({
    queryKey: ["questions", scope, status, query, token],
    enabled: !!token,
    queryFn: () =>
      api<{ items: Question[] }>(
        `/api/teaching-engine/question-bank?${new URLSearchParams({ ...(scope && { scope_id: scope }), ...(status && { status }), ...(query && { search: query }) })}`,
        { token },
      ),
  });
  const review = useMutation({
    mutationFn: ({ id, decision, comment = "" }: { id: string; decision: string; comment?: string }) =>
      post(
        `/api/teaching-engine/question-bank/${id}/review`,
        { decision, comment },
        token,
      ),
    onSuccess: () => {
      notify("题目状态已更新");
      setReviewQuestion(null);
      setReviewComment("");
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  const versions = useQuery({
    queryKey: ["question-versions", selectedQuestion?.id, token],
    enabled: !!token && !!selectedQuestion,
    queryFn: () => api<{
      entry: { stable_code: string; status: string; current_version_id?: string };
      items: Array<{ id: string; version_no: number; status: string; created_at: string; reviewed_at?: string; paper_references: number; attempt_references: number; review_notes?: string }>;
    }>(`/api/teaching-engine/question-bank/${selectedQuestion!.id}/versions`, { token }),
  });
  const retire = useMutation({
    mutationFn: (id: string) => post(`/api/teaching-engine/question-bank/${id}/retire`, { reason: "管理员从题库工作台停用，历史试卷与作答引用保留" }, token),
    onSuccess: () => {
      notify("题目已停用，历史试卷和作答记录仍保留原版本");
      setSelectedQuestion(null);
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h2>题库</h2>
          <p>知识点是题库基准；每道题分别记录命中权重、常见频率与知识点难度。</p>
        </div>
        <button
          className="primary"
          onClick={() => setCreate(true)}
          disabled={!token}
        >
          <Plus />
          录入题目
        </button>
      </div>
      <div className="toolbar">
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="">全部课程</option>
          {scopes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.grade} · {s.subject} · {s.name}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">全部状态</option>
          <option value="review">待审校</option>
          <option value="active">正式</option>
          <option value="draft">草稿</option>
          <option value="rejected">已退回</option>
        </select>
        <label>
          <Search />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索题干、标签或题号"
          />
        </label>
        <span>{questions.data?.items.length || 0} 条结果</span>
      </div>
      <section className="table-panel">
        {!token ? (
          <Empty>连接校区权限后查看题目正文</Empty>
        ) : questions.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>题目</th>
                <th>知识点三轴</th>
                <th>整题难度</th>
                <th>质量</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {questions.data.items.map((q) => (
                <tr key={q.id}>
                  <td className="question-cell">
                    <b>{q.stem?.text}</b>
                    <span>
                      {q.tags?.map((t) => (
                        <i key={t}>{t}</i>
                      ))}
                    </span>
                    <small>{q.stable_code} · v{q.version_no} · {q.grade} · {q.subject} · 发起人 {q.creator_name || "系统"}</small>
                  </td>
                  <td>
                    <div className="axis-list">
                      {q.knowledge_points?.length ? q.knowledge_points.map((k) => (
                        <div className="axis-row" key={`${k.id}-${k.role}`}>
                          <b>{k.name}</b>
                          <span>权 {Math.round((k.weight_axis ?? 0) * 100)}</span>
                          <span>频 {Math.round((k.frequency_score ?? 0) * 100)}</span>
                          <span>难 {Math.round((k.knowledge_difficulty ?? 0) * 100)}</span>
                        </div>
                      )) : <small>等待分类</small>}
                    </div>
                  </td>
                  <td>{Math.round(q.difficulty * 100)}%</td>
                  <td>{Math.round(q.quality_score * 100)}%</td>
                  <td>
                    <Status value={q.status} />
                  </td>
                  <td>
                    <button className="text-action" onClick={() => setSelectedQuestion(q)}>版本</button>
                    {q.status === "review" ? (
                      q.can_review ? <>
                        <button className="text-action" onClick={() => { setReviewQuestion(q); setReviewComment(""); }}>
                          打开审校
                        </button>
                      </> : <span className="muted review-locked">需由其他人员审校</span>
                    ) : q.status === "draft" ? (
                      <button
                        className="text-action"
                        onClick={() =>
                          review.mutate({ id: q.id, decision: "submit" })
                        }
                      >
                        提交审校
                      </button>
                    ) : (
                      <span className="muted">查看</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>没有符合条件的题目</Empty>
        )}
      </section>
      {create && (
        <QuestionDialog
          scopes={scopes}
          token={token}
          onClose={() => setCreate(false)}
          onDone={() => {
            setCreate(false);
            notify("题目已录入，自动分类任务已排队");
            qc.invalidateQueries({ queryKey: ["questions"] });
          }}
        />
      )}
      {selectedQuestion && <Modal title={`${selectedQuestion.stable_code} · 版本记录`} subtitle="已发布试卷和历史作答始终固定引用当时版本" onClose={() => setSelectedQuestion(null)}>
        <div className="version-list">
          {versions.data?.items.map((version) => <article key={version.id}>
            <div><b>版本 {version.version_no}</b><small>{fmtDate(version.created_at)}</small></div>
            <Status value={version.status} />
            <span>试卷引用 {version.paper_references}</span>
            <span>作答引用 {version.attempt_references}</span>
            {version.id === versions.data?.entry.current_version_id && <strong>当前正式版</strong>}
            {version.review_notes && <p>{version.review_notes}</p>}
          </article>)}
        </div>
        <div className="form-actions">
          {versions.data?.entry.status !== "retired" && <button className="quiet danger" onClick={() => {
            if (window.confirm("停用后不再用于新组卷，但历史试卷和作答仍保留。确认停用？")) retire.mutate(selectedQuestion.id);
          }}>停用题目</button>}
          <button className="quiet" onClick={() => setSelectedQuestion(null)}>关闭</button>
        </div>
      </Modal>}
      {reviewQuestion && <Modal title={`${reviewQuestion.stable_code} · 题目审校`}
        subtitle="核对题干、答案、解析和逐知识点三轴；当前操作会决定该版本能否进入正式组卷"
        onClose={() => { setReviewQuestion(null); setReviewComment(""); }}>
        <div className="question-review-sheet">
          <section><small>题干</small><h3>{reviewQuestion.stem?.text}</h3></section>
          {reviewQuestion.options?.length > 0 && <section><small>选项</small><ol className="review-options">
            {reviewQuestion.options.map((option, index) => <li key={index}>{String(option)}</li>)}
          </ol></section>}
          <div className="review-answer-grid">
            <section><small>标准答案</small><pre>{typeof reviewQuestion.answer === "string" ? reviewQuestion.answer : JSON.stringify(reviewQuestion.answer, null, 2)}</pre></section>
            <section><small>解析</small><p>{typeof reviewQuestion.explanation === "string" ? reviewQuestion.explanation : reviewQuestion.explanation?.text || "未填写解析"}</p></section>
          </div>
          <section><small>知识点与三轴</small><div className="axis-list review-axis-list">
            {reviewQuestion.knowledge_points.map((point) => <div className="axis-row" key={`${point.id}-${point.role}`}>
              <b>{point.name}</b><span>命中 {Math.round(point.hit_strength * 100)}</span>
              <span>基础 {Math.round(point.foundation_weight * 100)}</span><span>频率 {Math.round(point.frequency_score * 100)}</span>
              <span>难度 {Math.round(point.knowledge_difficulty * 100)}</span>
            </div>)}
          </div></section>
          <label>审校意见（必填）<textarea rows={4} value={reviewComment} onChange={(event) => setReviewComment(event.target.value)}
            placeholder="例如：答案唯一，解析步骤完整，知识点和难度标注准确。或写明退回原因。" /></label>
        </div>
        <div className="form-actions">
          <button className="quiet" onClick={() => { setReviewQuestion(null); setReviewComment(""); }}>取消</button>
          <button className="quiet danger" disabled={reviewComment.trim().length < 4 || review.isPending}
            onClick={() => review.mutate({ id: reviewQuestion.id, decision: "rejected", comment: reviewComment.trim() })}>退回修改</button>
          <button className="primary" disabled={reviewComment.trim().length < 4 || review.isPending}
            onClick={() => review.mutate({ id: reviewQuestion.id, decision: "approved", comment: reviewComment.trim() })}>批准为正式题</button>
        </div>
      </Modal>}
    </>
  );
}

function QuestionDialog({
  scopes,
  token,
  onClose,
  onDone,
}: {
  scopes: Scope[];
  token: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [scope, setScope] = useState("");
  const [error, setError] = useState("");
  const knowledge = useQuery({
    queryKey: ["dialog-knowledge", scope],
    enabled: !!scope,
    queryFn: () =>
      api<{ items: Knowledge[] }>(
        `/api/teaching-engine/knowledge-points?scope_id=${scope}`,
        { token },
      ),
  });
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const points = f.getAll("knowledge_points");
    if (!points.length) return setError("至少选择一个知识点");
    try {
      await post(
        "/api/teaching-engine/question-bank",
        {
          scope_id: scope,
          source_type: "manual",
          question_type: f.get("question_type"),
          difficulty: Number(f.get("difficulty")),
          max_score: Number(f.get("max_score")),
          stem: { text: f.get("stem") },
          options: String(f.get("options") || "")
            .split("\n")
            .filter(Boolean),
          answer: { value: f.get("answer") },
          knowledge_points: points,
          submit_for_review: true,
          auto_enrich: true,
        },
        token,
      );
      onDone();
    } catch (err) {
      setError((err as Error).message);
    }
  }
  return (
    <Modal
      title="录入题目"
      subtitle="保存后自动生成语义画像、向量和相似题候选"
      onClose={onClose}
    >
      <form className="form" onSubmit={submit}>
        <div className="form-grid">
          <label>
            课程范围
            <select
              required
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <option value="">请选择</option>
              {scopes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.grade} · {s.subject} · {s.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            题型
            <select name="question_type">
              <option value="single">单选题</option>
              <option value="multiple">多选题</option>
              <option value="judge">判断题</option>
              <option value="fill">填空题</option>
              <option value="short_answer">简答题</option>
              <option value="calculation">计算题</option>
            </select>
          </label>
          <label>
            初始难度
            <input
              name="difficulty"
              type="number"
              min="0"
              max="1"
              step=".05"
              defaultValue=".5"
            />
          </label>
          <label>
            分值
            <input name="max_score" type="number" min="1" defaultValue="10" />
          </label>
          <label className="wide">
            题干
            <textarea name="stem" required rows={4} />
          </label>
          <label className="wide">
            选项（每行一个）
            <textarea name="options" rows={4} />
          </label>
          <label className="wide">
            参考答案
            <input name="answer" required />
          </label>
          <fieldset className="wide">
            <legend>知识点</legend>
            <div className="check-grid">
              {knowledge.data?.items.map((k) => (
                <label key={k.id}>
                  <input type="checkbox" name="knowledge_points" value={k.id} />
                  <span>
                    {k.name}
                    <small>{k.code}</small>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        {error && <p className="form-error">{error}</p>}
        <footer>
          <button type="button" className="quiet" onClick={onClose}>
            取消
          </button>
          <button className="primary">保存并提交审校</button>
        </footer>
      </form>
    </Modal>
  );
}

type ExamSectionDraft = { title: string; question_types: string[]; question_count: number; score: number };
const examTypeLabel: Record<string,string> = { unit:"单元考",monthly:"月考",midterm:"期中考试",final:"期末考试",
  official:"正式考试",mock:"模拟考试",adaptive:"适应性考试",diagnostic:"阶段诊断" };
const examQuestionTypeLabel: Record<string,string> = { single:"选择题",multiple:"多选题",judge:"判断题",fill:"填空题",
  short_answer:"简答题",essay:"写作题",calculation:"计算题",composite:"综合题" };
function examSections(subject = ""): ExamSectionDraft[] {
  if (subject === "语文") return [
    { title:"积累与运用",question_types:["single","fill"],question_count:8,score:24 },
    { title:"阅读与鉴赏",question_types:["short_answer","composite"],question_count:6,score:36 },
    { title:"表达与写作",question_types:["essay"],question_count:1,score:40 },
  ];
  if (subject === "数学") return [
    { title:"选择题",question_types:["single"],question_count:10,score:30 },
    { title:"填空题",question_types:["fill"],question_count:6,score:18 },
    { title:"解答题",question_types:["calculation","short_answer","composite"],question_count:6,score:52 },
  ];
  if (subject === "英语") return [
    { title:"语言知识与阅读",question_types:["single","multiple"],question_count:20,score:50 },
    { title:"语言运用",question_types:["fill","short_answer"],question_count:8,score:30 },
    { title:"书面表达",question_types:["essay"],question_count:1,score:20 },
  ];
  if (["物理","化学"].includes(subject)) return [
    { title:"选择题",question_types:["single","multiple"],question_count:12,score:36 },
    { title:"实验与探究",question_types:["fill","short_answer","composite"],question_count:5,score:29 },
    { title:"综合计算",question_types:["calculation","composite"],question_count:4,score:35 },
  ];
  return [
    { title:"客观题",question_types:["single","multiple","judge"],question_count:20,score:50 },
    { title:"材料与综合题",question_types:["short_answer","composite"],question_count:5,score:50 },
  ];
}

function ExamCenter({ scopes, classes, token, notify }: { scopes: Scope[]; classes: Array<{id:string;name:string;campus_name?:string}>;
  token:string; notify:(value:string)=>void }) {
  const qc = useQueryClient();
  const [kind,setKind] = useState<"routine_exam"|"formal_exam">("routine_exam");
  const [create,setCreate] = useState(false);
  const [detail,setDetail] = useState("");
  const papers = useQuery({ queryKey:["exam-papers",kind,token], enabled:!!token,
    queryFn:()=>api<{items:Paper[]}>(`/api/teaching-engine/papers?assessment_kind=${kind}`,{token}) });
  const paperDetail = useQuery({ queryKey:["paper-detail",detail,token], enabled:!!detail && !!token,
    queryFn:()=>api<PaperDetail>(`/api/teaching-engine/papers/${detail}`,{token}) });
  const lifecycle = useMutation({ mutationFn:({id,action}:{id:string;action:"approve"|"publish"})=>
      post(`/api/teaching-engine/papers/${id}/lifecycle`,{action},token),
    onSuccess:(_,v)=>{ notify(v.action === "approve" ? "考卷已通过终审" : "考卷已发布到打印链路");
      qc.invalidateQueries({queryKey:["exam-papers"]}); qc.invalidateQueries({queryKey:["paper-detail",v.id]}); },
    onError:(e)=>notify((e as Error).message) });
  return <>
    <div className="page-head"><div><h2>考卷管理</h2><p>日常考试与中考、高考规范考卷分层管理，共用正式题库、答题卡、打印和成绩回流。</p></div>
      <button className="primary" onClick={()=>setCreate(true)}><Plus />创建{kind === "routine_exam" ? "日常考卷" : "规范考卷"}</button></div>
    <div className="exam-kind-switch">
      <button className={kind === "routine_exam" ? "active" : ""} onClick={()=>setKind("routine_exam")}><b>日常考试</b><span>单元考 · 月考 · 期中 · 期末 · 阶段诊断</span></button>
      <button className={kind === "formal_exam" ? "active" : ""} onClick={()=>setKind("formal_exam")}><b>正式考试</b><span>中考标准 · 高考标准 · 模拟与适应性考试</span></button>
    </div>
    <section className="table-panel">{papers.data?.items.length ? <table><thead><tr><th>考卷</th><th>教学班</th><th>题数 / 总分</th><th>状态</th><th>操作</th></tr></thead>
      <tbody>{papers.data.items.map(p=><tr key={p.id}><td><b>{p.title}</b><small>{p.paper_code}</small></td><td>{p.class_name || "—"}</td>
        <td>{p.question_count} 题 / {p.total_score} 分</td><td><Status value={p.status}/></td><td>
          <button className="text-action" onClick={()=>setDetail(p.id)}>审阅</button>
          {p.status === "review" && <button className="text-action" onClick={()=>lifecycle.mutate({id:p.id,action:"approve"})}>通过终审</button>}
          {p.status === "approved" && <button className="text-action" onClick={()=>lifecycle.mutate({id:p.id,action:"publish"})}>发布</button>}
        </td></tr>)}</tbody></table> : <Empty>尚未创建此类考卷</Empty>}</section>
    {create && <ExamBlueprintDialog kind={kind} scopes={scopes} classes={classes} token={token} onClose={()=>setCreate(false)} onDone={()=>{
      setCreate(false); notify("考卷已生成并进入人工终审"); qc.invalidateQueries({queryKey:["exam-papers"]}); }} />}
    {detail && <PaperDetailDialog data={paperDetail.data} loading={paperDetail.isLoading} error={paperDetail.error as Error|null}
      token={token} notify={notify} onChanged={()=>{qc.invalidateQueries({queryKey:["paper-detail",detail]});qc.invalidateQueries({queryKey:["exam-papers"]});}}
      onClose={()=>setDetail("")} />}
  </>;
}

function ExamBlueprintDialog({kind,scopes,classes,token,onClose,onDone}:{kind:"routine_exam"|"formal_exam";scopes:Scope[];
  classes:Array<{id:string;name:string;campus_name?:string}>;token:string;onClose:()=>void;onDone:()=>void}) {
  const [scopeId,setScopeId] = useState("");
  const [standard,setStandard] = useState<"zhongkao"|"gaokao">("zhongkao");
  const [sections,setSections] = useState<ExamSectionDraft[]>(examSections());
  const [error,setError] = useState(""); const [saving,setSaving] = useState(false);
  const selectedScope = scopes.find(s=>s.id === scopeId);
  const knowledge = useQuery({queryKey:["formal-exam-knowledge",scopeId,token],enabled:!!scopeId&&!!token,
    queryFn:()=>api<{items:Knowledge[]}>(`/api/teaching-engine/knowledge-points?scope_id=${scopeId}`,{token})});
  useEffect(()=>{ if(selectedScope) setSections(examSections(selectedScope.subject)); },[scopeId]);
  const totalQuestions = sections.reduce((n,s)=>n+s.question_count,0), totalScore=sections.reduce((n,s)=>n+s.score,0);
  async function submit(event:FormEvent<HTMLFormElement>){event.preventDefault();
    if(!scopeId || !sections.length) return setError("请选择课程范围并配置卷面结构");
    if(kind === "formal_exam" && standard === "gaokao" && selectedScope?.school_stage === "junior")
      return setError("高考规范只能使用高中独立知识库，系统已阻止把初中知识点用于高考组卷");
    setSaving(true);setError(""); const form=new FormData(event.currentTarget);
    try { const bp=await post<{id:string}>("/api/teaching-engine/blueprints",{
      class_group_id:String(form.get("class_group_id")||""),scope_id:scopeId,title:String(form.get("title")||""),generation_mode:"class",
      assessment_kind:kind,exam_standard:kind === "formal_exam" ? standard : null,exam_type:String(form.get("exam_type")),
      duration_minutes:Number(form.get("duration_minutes")),scheduled_for:String(form.get("scheduled_for")||"")||null,
      question_count:totalQuestions,total_score:totalScore,difficulty_min:.1,difficulty_max:.9,
      knowledge_point_ids:(knowledge.data?.items||[]).filter(p=>p.depth>0).map(p=>p.id),sections,
      min_knowledge_coverage:kind === "formal_exam" ? .9 : .75,require_explanation:true,
    },token); await post(`/api/teaching-engine/blueprints/${bp.id}/generate`,{},token); onDone(); }
    catch(e){setError((e as Error).message);setSaving(false);} }
  const purposes=kind === "routine_exam" ? [["unit","单元考"],["monthly","月考"],["midterm","期中考试"],["final","期末考试"],["diagnostic","阶段诊断"]]
    : [["official","正式考试"],["mock","模拟考试"],["adaptive","适应性考试"]];
  return <Modal title={kind === "routine_exam" ? "创建日常考卷" : "创建中考 / 高考规范考卷"}
    subtitle="按学科蓝图分区抽题；生成后必须经过人工终审才能发布" onClose={onClose}>
    <form className="form" onSubmit={submit}><div className="form-grid">
      {kind === "formal_exam" && <label>命题标准<select value={standard} onChange={e=>setStandard(e.target.value as typeof standard)}><option value="zhongkao">中考命题标准</option><option value="gaokao">高考命题标准</option></select></label>}
      <label>考试用途<select name="exam_type">{purposes.map(([v,l])=><option value={v} key={v}>{l}</option>)}</select></label>
      <label>教学班<select name="class_group_id" required><option value="">请选择</option>{classes.map(c=><option value={c.id} key={c.id}>{c.campus_name ? `${c.campus_name} · `:""}{c.name}</option>)}</select></label>
      <label>课程范围<select required value={scopeId} onChange={e=>setScopeId(e.target.value)}><option value="">请选择年级与学科</option>{scopes.map(s=><option value={s.id} key={s.id}>{s.grade} · {s.subject} · {s.name}</option>)}</select></label>
      <label className="wide">考卷标题<input name="title" required placeholder="例如：九年级数学中考模拟卷（一）" /></label>
      <label>考试日期<input name="scheduled_for" type="date" /></label><label>考试时长（分钟）<input name="duration_minutes" type="number" min="10" max="300" defaultValue="120" required /></label>
      <fieldset className="wide exam-structure"><legend>卷面结构</legend>{sections.map((section,index)=><article key={index}>
        <input aria-label="部分名称" value={section.title} onChange={e=>setSections(items=>items.map((x,i)=>i===index?{...x,title:e.target.value}:x))}/>
        <select value={section.question_types[0]} onChange={e=>setSections(items=>items.map((x,i)=>i===index?{...x,question_types:[e.target.value]}:x))}>{Object.entries(examQuestionTypeLabel).map(([v,l])=><option value={v} key={v}>{l}</option>)}</select>
        <label>题数<input type="number" min="1" value={section.question_count} onChange={e=>setSections(items=>items.map((x,i)=>i===index?{...x,question_count:Number(e.target.value)}:x))}/></label>
        <label>分值<input type="number" min="1" value={section.score} onChange={e=>setSections(items=>items.map((x,i)=>i===index?{...x,score:Number(e.target.value)}:x))}/></label>
        <button type="button" className="icon-btn danger" onClick={()=>setSections(items=>items.filter((_,i)=>i!==index))}>×</button></article>)}
        <div className="structure-total"><button type="button" className="quiet small" onClick={()=>setSections(items=>[...items,{title:"新增部分",question_types:["short_answer"],question_count:1,score:10}])}><Plus/>增加部分</button>
          <b>共 {totalQuestions} 题 · {totalScore} 分</b></div></fieldset>
      <div className="wide exam-quality-note"><ShieldCheck/><div><b>{kind === "formal_exam" ? "正式考试质量门禁" : "日常考试审校门禁"}</b>
        <span>覆盖当前课程范围全部基础知识点；校验题型、题数、分值、答案解析、学科隔离和重复题；生成后进入人工终审。</span></div></div>
    </div>{error&&<p className="form-error">{error}</p>}<footer><button type="button" className="quiet" onClick={onClose}>取消</button><button className="primary" disabled={saving}>{saving?"正在组卷":"生成并进入终审"}</button></footer></form>
  </Modal>;
}

function AssessmentPage({
  scopes,
  classes,
  token,
  allCampuses,
  classFocus,
  clearClassFocus,
  notify,
}: {
  scopes: Scope[];
  classes: Array<{ id: string; name: string; campus_name?: string }>;
  token: string;
  allCampuses: boolean;
  classFocus: string;
  clearClassFocus: () => void;
  notify: (s: string) => void;
}) {
  const qc = useQueryClient();
  const [create, setCreate] = useState(false);
  const [generate, setGenerate] = useState(false);
  const [selectedPapers, setSelectedPapers] = useState<string[]>([]);
  const [detailPaperId, setDetailPaperId] = useState("");
  const [pickup, setPickup] = useState<{ pickup_url: string; qr_svg: string; title?: string } | null>(null);
  const [paperAction, setPaperAction] = useState<{ paper: Paper; action: "close" | "cancel" } | null>(null);
  const papers = useQuery({
    queryKey: ["papers", classFocus, token],
    enabled: !!token,
    queryFn: () =>
      api<{ items: Paper[] }>(`/api/teaching-engine/papers?assessment_kind=quiz${classFocus ? `&class_group_id=${classFocus}` : ""}`, { token }),
  });
  const batches = useQuery({
    queryKey: ["print-batches", token], enabled: !!token,
    queryFn: () => api<{ items: Array<Record<string, any>> }>("/api/teaching-engine/print-batches", { token }),
  });
  const paperDetail = useQuery({
    queryKey: ["paper-detail", detailPaperId, token], enabled: !!token && !!detailPaperId,
    queryFn: () => api<PaperDetail>(`/api/teaching-engine/papers/${detailPaperId}`, { token }),
  });
  const createBatch = useMutation({
    mutationFn: () => post<{ pickup_url: string; qr_svg: string; title: string }>("/api/teaching-engine/print-batches", {
      title: `办公室打印 ${new Date().toLocaleDateString("zh-CN")}`,
      paper_ids: selectedPapers, copies: 1, pickup_minutes: 120,
    }, token),
    onSuccess: (data) => {
      setPickup(data); setSelectedPapers([]); qc.invalidateQueries({ queryKey: ["print-batches"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  const rotatePickup = useMutation({
    mutationFn: (id: string) => post<{ pickup_url: string; qr_svg: string }>(`/api/teaching-engine/print-batches/${id}/pickup-token`, {}, token),
    onSuccess: (data) => setPickup(data),
    onError: (e) => notify((e as Error).message),
  });
  const lifecycle = useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: "publish" | "close" | "cancel"; reason?: string }) =>
      post(`/api/teaching-engine/papers/${id}/lifecycle`, { action, reason }, token),
    onSuccess: (_, variables) => {
      setPaperAction(null); setSelectedPapers([]); qc.invalidateQueries({ queryKey: ["papers"] });
      qc.invalidateQueries({ queryKey: ["paper-detail", variables.id] });
      notify(variables.action === "publish" ? "试卷已发布，可以建立打印批次" : variables.action === "close" ? "试卷已结卷" : "试卷已取消");
    },
    onError: (e) => notify((e as Error).message),
  });
  async function print(p: Paper) {
    const r = await fetch(`/api/teaching-engine/papers/${p.id}/print`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return notify("打印页生成失败");
    const w = open("about:blank", "_blank");
    w?.document.write(await r.text());
    w?.document.close();
  }
  return (
    <>
      <div className="page-head">
        <div>
          <h2>课堂小测</h2>
          <p>按班级、知识点和难度范围组卷，办公室可直接打印试卷与答题卡。</p>
        </div>
        <div className="page-actions">
          <button className="quiet" disabled={!selectedPapers.length || createBatch.isPending} onClick={() => createBatch.mutate()}>
            <Printer />创建打印批次（{selectedPapers.length}）
          </button>
          <button className="quiet" disabled={!token} onClick={() => setGenerate(true)}>
            <Sparkles />教学助理出题
          </button>
          <button className="primary" disabled={!token} onClick={() => setCreate(true)}>
            <Plus />用正式题组卷
          </button>
        </div>
      </div>
      {classFocus && <div className="context-filter"><span>当前只看：<b>{classes.find((item) => item.id === classFocus)?.name || "指定教学班"}</b></span><button className="text-action" onClick={clearClassFocus}>查看全部班级</button></div>}
      <section className="table-panel">
        {papers.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th className="check-cell">选择</th>
                <th>试卷</th>
                <th>教学班</th>
                <th>题数 / 总分</th>
                <th>生成时间</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {papers.data.items.map((p) => (
                <tr key={p.id}>
                  <td className="check-cell"><input type="checkbox" disabled={!['published','printed'].includes(p.status)} checked={selectedPapers.includes(p.id)}
                    onChange={(e) => setSelectedPapers((items) => e.target.checked ? [...items, p.id] : items.filter((id) => id !== p.id))} /></td>
                  <td>
                    <b>{p.title}</b>
                    <small>{p.paper_code}</small>
                  </td>
                  <td>{p.student_name ? `个性化 · ${p.student_name}` : p.class_name || "—"}</td>
                  <td>
                    {p.question_count} 题 / {p.total_score} 分
                  </td>
                  <td>{fmtDate(p.generated_at)}</td>
                  <td>
                    <Status value={p.status} />
                  </td>
                  <td>
                    <button className="text-action" onClick={() => setDetailPaperId(p.id)}>详情</button>
                    {p.status === "approved" && <button className="text-action" onClick={() => lifecycle.mutate({ id: p.id, action: "publish" })}>发布</button>}
                    {["published", "printed", "grading", "closed"].includes(p.status) && <button className="text-action" onClick={() => print(p)}>
                      <Printer />
                      打印
                    </button>}
                    {["published", "printed", "grading"].includes(p.status) && <button className="text-action" onClick={() => setPaperAction({ paper: p, action: "close" })}>结卷</button>}
                    {["approved", "published"].includes(p.status) && <button className="text-action danger" onClick={() => setPaperAction({ paper: p, action: "cancel" })}>取消</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>尚未生成试卷</Empty>
        )}
      </section>
      <section className="print-batch-strip">
        <div className="section-title"><div><h3>办公室打印批次</h3><p>生成临时取卷二维码，办公室扫码后只看到本批次试卷；二维码到期或完成后失效。</p></div></div>
        <div className="batch-list">
          {batches.data?.items.length ? batches.data.items.slice(0, 6).map((batch) => <article key={batch.id}>
            <div><b>{batch.title}</b><span>{batch.paper_count} 份试卷 · {batch.campus_name} · {fmtDate(batch.created_at)}</span></div>
            <Status value={batch.status} />
            {!['completed','cancelled'].includes(batch.status) && <button className="quiet small" onClick={() => rotatePickup.mutate(batch.id)}>显示取卷码</button>}
          </article>) : <Empty>选择上方试卷后创建第一个打印批次</Empty>}
        </div>
      </section>
      {create && (
        <AssessmentDialog
          scopes={scopes}
          classes={classes}
          initialClassId={classFocus}
          token={token}
          onClose={() => setCreate(false)}
          onDone={() => {
            setCreate(false);
            notify("小测试卷已生成");
            qc.invalidateQueries({ queryKey: ["papers"] });
          }}
        />
      )}
      {generate && <QuizGenerationDialog scopes={scopes} token={token} allCampuses={allCampuses} onClose={() => setGenerate(false)}
        onDone={() => { setGenerate(false); notify("候选题正在生成，完成后请到“教学助理审校”确认"); }} />}
      {pickup && <Modal title="办公室取卷二维码" subtitle="二维码默认两小时有效；扫描后可直接打印并标记完成" onClose={() => setPickup(null)}>
        <div className="pickup-card"><div dangerouslySetInnerHTML={{ __html: pickup.qr_svg }} />
          <a href={pickup.pickup_url} target="_blank" rel="noreferrer">打开取卷页</a><small>{pickup.pickup_url}</small></div>
      </Modal>}
      {paperAction && <PaperLifecycleDialog value={paperAction} busy={lifecycle.isPending}
        onClose={() => setPaperAction(null)} onSubmit={(reason) => lifecycle.mutate({ id: paperAction.paper.id, action: paperAction.action, reason })} />}
      {detailPaperId && <PaperDetailDialog data={paperDetail.data} loading={paperDetail.isLoading} error={paperDetail.error as Error | null}
        token={token} notify={notify} onChanged={() => { qc.invalidateQueries({ queryKey: ["paper-detail", detailPaperId] }); qc.invalidateQueries({ queryKey: ["papers"] }); }}
        onClose={() => setDetailPaperId("")} />}
    </>
  );
}

function PaperDetailDialog({ data, loading, error, token, notify, onChanged, onClose }: {
  data?: PaperDetail; loading: boolean; error: Error | null; token: string; notify: (value: string) => void;
  onChanged: () => void; onClose: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [draft, setDraft] = useState<Array<{ question_id: string; score: number }>>([]);
  useEffect(() => {
    if (data && !editing) setDraft(data.items.map((item) => ({ question_id: item.question_id, score: item.score })));
  }, [data, editing]);
  const candidates = useQuery({
    queryKey: ["paper-candidates", data?.paper.scope_id, token],
    enabled: editing && !!data?.paper.scope_id && !!token,
    queryFn: () => api<{ items: Question[] }>(
      `/api/teaching-engine/question-bank?scope_id=${data!.paper.scope_id}&status=active&limit=200`, { token }),
  });
  const choices = useMemo(() => {
    const values = new Map<string, { id: string; label: string }>();
    data?.items.forEach((item) => values.set(item.question_id, { id: item.question_id, label: `${item.stable_code} · ${item.stem?.text || "题干未录入"}` }));
    candidates.data?.items.forEach((item) => values.set(item.id, { id: item.id, label: `${item.stable_code} · ${item.stem?.text || "题干未录入"}` }));
    return [...values.values()];
  }, [data, candidates.data]);
  const saveComposition = useMutation({
    mutationFn: () => api(`/api/teaching-engine/papers/${data!.paper.id}/composition`, {
      method: "PUT", token, body: JSON.stringify({ expected_version: data!.paper.version_no, items: draft, reason: reason.trim() }),
    }),
    onSuccess: () => { setEditing(false); setReason(""); notify("试卷组成已更新，发布前版本号已递增"); onChanged(); },
    onError: (value) => notify((value as Error).message),
  });
  const move = (index: number, direction: -1 | 1) => setDraft((items) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return items;
    const next = [...items]; [next[index], next[target]] = [next[target], next[index]]; return next;
  });
  const draftTotal = draft.reduce((sum, item) => sum + Number(item.score || 0), 0);
  return <Modal title={data?.paper.title || "试卷详情"} subtitle={data?.paper.paper_code || "查看题目版本、作答统计与状态轨迹"} onClose={onClose}>
    <div className="paper-detail">
      {loading && <Empty>正在读取试卷档案</Empty>}
      {error && <p className="form-error">{error.message}</p>}
      {data && <>
        <div className="paper-detail-metrics">
          <span>当前状态<strong><Status value={data.paper.status} /></strong></span>
          <span>组成版本<strong>v{data.paper.version_no}</strong></span>
          <span>题目<strong>{data.items.length} 道</strong></span>
          <span>总分<strong>{data.paper.total_score} 分</strong></span>
          <span>已回流作答<strong>{data.paper.attempt_count} 条</strong></span>
          <span>打印批次<strong>{data.paper.print_batch_count} 个</strong></span>
        </div>
        {data.paper.lifecycle_note && <div className="paper-note"><b>状态说明</b><p>{data.paper.lifecycle_note}</p></div>}
        {data.paper.status === "approved" && !editing && <div className="paper-composition-gate"><div><b>发布前组成检查</b>
          <span>可调整题序、替换为同课程正式题并重新分配分值；发布或打印后永久锁定。</span></div>
          <button className="quiet" onClick={() => { setDraft(data.items.map((item) => ({ question_id: item.question_id, score: item.score }))); setEditing(true); }}>编辑试卷组成</button></div>}
        {editing && <section className="paper-composer"><header><div><h3>编辑试卷组成</h3><p>只列出当前课程范围内已通过审校的正式题，不能加入待审题或其他学科题。</p></div>
          <strong>{draft.length} 题 · {draftTotal.toFixed(2)} 分</strong></header>
          <div className="paper-compose-list">{draft.map((item, index) => <article key={`${item.question_id}-${index}`}>
            <b>{index + 1}</b><select value={item.question_id} onChange={(event) => setDraft((items) => items.map((value, position) => position === index ? { ...value, question_id: event.target.value } : value))}>
              {choices.map((choice) => <option key={choice.id} value={choice.id}>{choice.label}</option>)}</select>
            <label>分值<input type="number" min="0.5" max="1000" step="0.5" value={item.score}
              onChange={(event) => setDraft((items) => items.map((value, position) => position === index ? { ...value, score: Number(event.target.value) } : value))} /></label>
            <div><button className="icon-btn" disabled={index === 0} aria-label={`第${index + 1}题上移`} onClick={() => move(index, -1)}>↑</button>
              <button className="icon-btn" disabled={index === draft.length - 1} aria-label={`第${index + 1}题下移`} onClick={() => move(index, 1)}>↓</button>
              <button className="icon-btn danger" disabled={draft.length === 1} aria-label={`删除第${index + 1}题`} onClick={() => setDraft((items) => items.filter((_, position) => position !== index))}>×</button></div>
          </article>)}</div>
          <div className="paper-compose-add"><button className="quiet" disabled={!choices.find((choice) => !draft.some((item) => item.question_id === choice.id))}
            onClick={() => { const candidate = choices.find((choice) => !draft.some((item) => item.question_id === choice.id)); if (candidate) setDraft((items) => [...items, { question_id: candidate.id, score: 1 }]); }}><Plus />增加一道正式题</button></div>
          <label className="approval-comment">调整依据<textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="例如：替换重复题，并调整题序与分值" /></label>
          <footer className="modal-actions"><button className="quiet" onClick={() => { setEditing(false); setReason(""); }}>放弃修改</button>
            <button className="primary" disabled={saveComposition.isPending || reason.trim().length < 2 || !draft.length || draft.some((item) => !item.question_id || item.score <= 0)}
              onClick={() => saveComposition.mutate()}>{saveComposition.isPending ? "保存中" : `保存 v${data.paper.version_no + 1}`}</button></footer>
        </section>}
        {!editing && <div className="paper-detail-columns">
          <section><h3>题目与作答</h3><div className="paper-item-list">{data.items.map((item) => {
            const rate = item.attempt_count ? Math.round(item.correct_count / item.attempt_count * 100) : null;
            return <article key={item.question_id}><b>{item.position}. {item.stem?.text || "题干未录入"}</b>
              <span>{item.stable_code} · v{item.version_no} · {item.score} 分</span>
              <small>{item.attempt_count ? `${item.attempt_count} 次作答 · 正确率 ${rate}%` : "尚无作答"}</small></article>;
          })}</div></section>
          <section><h3>状态轨迹</h3><div className="paper-event-list">{data.events.length ? data.events.map((event) => <article key={event.id}>
            <i /><div><b>{event.event_type === "composition_updated" ? "已调整试卷组成" : statusLabel[event.to_status || event.event_type] || event.event_type}</b>
              <span>{event.actor_name} · {fmtDate(event.created_at)}</span>{event.reason && <p>{event.reason}</p>}</div>
          </article>) : <Empty>尚无状态事件</Empty>}</div></section>
        </div>}
      </>}
    </div>
  </Modal>;
}

function PaperLifecycleDialog({ value, busy, onClose, onSubmit }: {
  value: { paper: Paper; action: "close" | "cancel" }; busy: boolean;
  onClose: () => void; onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const cancelling = value.action === "cancel";
  return <Modal title={cancelling ? "取消试卷" : "确认结卷"}
    subtitle={cancelling ? "取消后不能打印或回流成绩；已有作答或打印批次时系统会阻止取消。" : "结卷会保留试卷、答题、成绩和学情证据，只结束本次测评。"}
    onClose={onClose}>
    <div className="form-grid"><label className="wide">{cancelling ? "取消原因（必填）" : "结卷说明（选填）"}
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={cancelling ? "说明为什么取消本次测评" : "例如：本班答题卡已全部回流"} />
    </label></div>
    <footer className="modal-actions"><button className="quiet" onClick={onClose}>返回</button>
      <button className={cancelling ? "quiet danger" : "primary"} disabled={busy || (cancelling && reason.trim().length < 2)} onClick={() => onSubmit(reason.trim())}>
        {busy ? "处理中" : cancelling ? "确认取消" : "确认结卷"}
      </button></footer>
  </Modal>;
}

function QuizGenerationDialog({ scopes, token, allCampuses, onClose, onDone }: {
  scopes: Scope[]; token: string; allCampuses: boolean; onClose: () => void; onDone: () => void;
}) {
  const [scopeId, setScopeId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const knowledge = useQuery({
    queryKey: ["generation-knowledge", scopeId, token], enabled: !!scopeId && !!token,
    queryFn: () => api<{ items: Knowledge[] }>(`/api/teaching-engine/knowledge-points?scope_id=${scopeId}`, { token }),
  });
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const pointIds = form.getAll("knowledge_point_ids").map(String);
    const types = form.getAll("question_types").map(String);
    if (!pointIds.length) return setError("请至少选择一个知识点");
    if (!types.length) return setError("请至少选择一种题型");
    setSaving(true); setError("");
    try {
      await post(`/api/teaching-engine/agent/runs${allCampuses ? "?scope=all" : ""}`, {
        run_type: "class_quiz", target_type: "curriculum_scope", target_id: scopeId,
        state: { scope_id: scopeId, title: String(form.get("title") || ""),
          objective: String(form.get("objective") || ""), question_count: Number(form.get("question_count")),
          difficulty_min: Number(form.get("difficulty_min")), difficulty_max: Number(form.get("difficulty_max")),
          question_types: types, knowledge_point_ids: pointIds },
      }, token);
      onDone();
    } catch (err) { setError((err as Error).message); setSaving(false); }
  }
  return <Modal title="生成候选题" subtitle="生成结果先进入教师审批；批准后仍需逐题审校，不会直接发布" onClose={onClose}>
    <form className="form" onSubmit={submit}>
      <div className="form-grid">
        <label>课程范围<select required value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
          <option value="">请选择年级与学科</option>{scopes.map((s) => <option key={s.id} value={s.id}>{s.grade} · {s.subject} · {s.name}</option>)}
        </select></label>
        <label>题目数量<input name="question_count" type="number" min="1" max="20" defaultValue="5" required /></label>
        <label className="wide">小测标题<input name="title" required placeholder="例如：一次函数课堂检测" /></label>
        <label className="wide">本次目标<textarea name="objective" rows={2} placeholder="说明本节课要检测的能力，教学助理会据此生成" /></label>
        <label>最低难度<input name="difficulty_min" type="number" min="0" max="1" step=".05" defaultValue=".25" required /></label>
        <label>最高难度<input name="difficulty_max" type="number" min="0" max="1" step=".05" defaultValue=".75" required /></label>
        <fieldset className="wide"><legend>题型</legend><div className="inline-checks">
          {[["single","单选"],["fill","填空"],["short_answer","简答"],["calculation","计算"]].map(([value,label]) =>
            <label key={value}><input type="checkbox" name="question_types" value={value} defaultChecked={value === "single" || value === "fill"} />{label}</label>)}
        </div></fieldset>
        <fieldset className="wide"><legend>知识点</legend><div className="check-grid compact">
          {knowledge.isFetching ? <span className="muted">正在读取知识点…</span> : knowledge.data?.items.filter((k) => k.depth > 0).map((k) =>
            <label key={k.id}><input type="checkbox" name="knowledge_point_ids" value={k.id} /><span>{k.name}<small>{k.code}</small></span></label>)}
        </div></fieldset>
      </div>
      {error && <p className="form-error">{error}</p>}
      <footer><button type="button" className="quiet" onClick={onClose}>取消</button>
        <button className="primary" disabled={saving}>{saving ? "提交中…" : "提交生成任务"}</button></footer>
    </form>
  </Modal>;
}

function AssessmentDialog({
  scopes,
  classes,
  token,
  initialClassId,
  initialScopeId = "",
  initialKnowledgeIds = [],
  initialTitle = "",
  onClose,
  onDone,
}: {
  scopes: Array<Pick<Scope, "id" | "grade" | "subject" | "name">>;
  classes: Array<{ id: string; name: string; campus_name?: string }>;
  token: string;
  initialClassId?: string;
  initialScopeId?: string;
  initialKnowledgeIds?: string[];
  initialTitle?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [mode, setMode] = useState<"class" | "layered" | "personalized">("class");
  const [scopeId, setScopeId] = useState(initialScopeId);
  const [studentTerm, setStudentTerm] = useState("");
  const knowledge = useQuery({
    queryKey: ["assessment-knowledge", scopeId, token], enabled: !!scopeId && !!token,
    queryFn: () => api<{ items: Knowledge[] }>(`/api/teaching-engine/knowledge-points?scope_id=${scopeId}`, { token }),
  });
  const students = useQuery({
    queryKey: ["assessment-students", studentTerm, token],
    enabled: mode === "personalized" && studentTerm.trim().length > 0,
    queryFn: () => api<{ items: Array<{ id: string; name: string; external_student_no?: string; campus_name: string }> }>(
      `/api/teaching-engine/learning/students?query=${encodeURIComponent(studentTerm)}`, { token }),
  });
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const v = Object.fromEntries(form);
    const knowledgePointIds = form.getAll("knowledge_point_ids").map(String);
    if (!knowledgePointIds.length) return setError("请至少选择一个本次要检测的知识点");
    try {
      const bp = await post<{ id: string }>(
        "/api/teaching-engine/blueprints",
        {
          ...v,
          question_count: Number(v.question_count),
          total_score: Number(v.total_score),
          difficulty_min: Number(v.difficulty_min),
          difficulty_max: Number(v.difficulty_max),
          knowledge_point_ids: knowledgePointIds,
        },
        token,
      );
      await post(
        `/api/teaching-engine/blueprints/${bp.id}/generate`,
        {},
        token,
      );
      onDone();
    } catch (err) {
      setError((err as Error).message);
    }
  }
  return (
    <Modal
      title="创建班级小测"
      subtitle="只有已通过审校的正式题目会进入试卷"
      onClose={onClose}
    >
      <form className="form" onSubmit={submit}>
        <div className="form-grid">
          <label>
            组卷方式
            <select name="generation_mode" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="class">班级共用卷</option>
              <option value="layered">班级分层卷</option>
              <option value="personalized">学生个性化卷</option>
            </select>
          </label>
          {mode !== "personalized" ? <label>
            教学班
            <select name="class_group_id" required defaultValue={initialClassId || ""}>
              <option value="">请选择</option>
              {classes.map((c) => (
                <option value={c.id} key={c.id}>
                  {c.campus_name ? `${c.campus_name} · ` : ""}
                  {c.name}
                </option>
              ))}
            </select>
          </label> : <label className="wide student-picker">
            学生
            <input value={studentTerm} onChange={(e) => setStudentTerm(e.target.value)} placeholder="先输入姓名或学号检索" />
            <select name="student_id" required defaultValue="">
              <option value="">请选择检索到的学生</option>
              {students.data?.items.map((student) => <option value={student.id} key={student.id}>
                {student.name} · {student.external_student_no || "无学号"} · {student.campus_name}
              </option>)}
            </select>
          </label>}
          <label>
            课程范围
            <select name="scope_id" required value={scopeId} onChange={(event) => setScopeId(event.target.value)}>
              <option value="">请选择</option>
              {scopes.map((s) => (
                <option value={s.id} key={s.id}>
                  {s.grade} · {s.subject} · {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="wide">
            试卷标题
            <input
              name="title"
              required
              defaultValue={initialTitle}
              placeholder="例如：七年级数学 9 月 1 日课堂小测"
            />
          </label>
          <label>
            测评日期
            <input name="scheduled_for" type="date" />
          </label>
          <label>
            题目数量
            <input
              name="question_count"
              type="number"
              min="1"
              max="100"
              defaultValue="10"
            />
          </label>
          <label>
            总分
            <input
              name="total_score"
              type="number"
              min="1"
              defaultValue="100"
            />
          </label>
          <label>
            最低难度
            <input
              name="difficulty_min"
              type="number"
              min="0"
              max="1"
              step=".05"
              defaultValue=".2"
            />
          </label>
          <label>
            最高难度
            <input
              name="difficulty_max"
              type="number"
              min="0"
              max="1"
              step=".05"
              defaultValue=".8"
            />
          </label>
          <fieldset className="wide"><legend>本次检测知识点</legend><div className="check-grid compact">
            {knowledge.isFetching ? <span className="muted">正在读取知识点…</span> : knowledge.data?.items.filter((point) => point.depth > 0).map((point) =>
              <label key={point.id}><input type="checkbox" name="knowledge_point_ids" value={point.id} defaultChecked={initialKnowledgeIds.includes(point.id)} />
                <span>{point.name}<small>{point.code}</small></span></label>)}
          </div></fieldset>
        </div>
        {error && <p className="form-error">{error}</p>}
        <footer>
          <button type="button" className="quiet" onClick={onClose}>
            取消
          </button>
          <button className="primary">生成试卷</button>
        </footer>
      </form>
    </Modal>
  );
}

function GradingPage({ token, classFocus, clearClassFocus, notify }: {
  token: string; classFocus: string; clearClassFocus: () => void; notify: (s: string) => void;
}) {
  const qc = useQueryClient();
  const [reviewing, setReviewing] = useState<Record<string, any> | null>(null);
  const [studentNo, setStudentNo] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [reviewResponses, setReviewResponses] = useState<Array<Record<string, any>>>([]);
  const events = useQuery({
    queryKey: ["grading", classFocus, token],
    enabled: !!token,
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>(
        `/api/teaching-engine/grading/events${classFocus ? `?class_group_id=${classFocus}` : ""}`,
        { token },
      ),
    refetchInterval: 15000,
  });
  const detail = useQuery({
    queryKey: ["grading-detail", reviewing?.id, token],
    enabled: !!token && !!reviewing?.id,
    queryFn: () => api<{ event: Record<string, any>; items: Array<Record<string, any>>; recognized_count: number; expected_count: number }>(
      `/api/teaching-engine/grading/events/${reviewing!.id}`, { token }),
  });
  useEffect(() => {
    if (!detail.data || !reviewing) return;
    setReviewResponses(detail.data.items.map((item) => ({
      position: item.position,
      answer: item.recognized?.answer ?? "",
      score: Number(item.recognized?.score ?? (item.recognized?.is_correct ? item.max_score : 0)),
      is_correct: Boolean(item.recognized?.is_correct),
    })));
  }, [detail.data, reviewing?.id]);
  function updateResponse(position: number, patch: Record<string, any>) {
    setReviewResponses((items) => items.map((item) => item.position === position ? { ...item, ...patch } : item));
  }
  const review = useMutation({
    mutationFn: (decision: "apply" | "reject") =>
      post(
        `/api/teaching-engine/grading/events/${reviewing!.id}/review`,
        {
          decision,
          external_student_no: studentNo.trim() || undefined,
          comment: reviewComment.trim() || (decision === "apply" ? "教师确认识别结果" : "教师拒绝识别结果"),
          responses: decision === "apply" ? reviewResponses : undefined,
        },
        token,
      ),
    onSuccess: () => {
      notify("批阅复核结果已保存");
      setReviewing(null); setStudentNo(""); setReviewComment(""); setReviewResponses([]);
      qc.invalidateQueries({ queryKey: ["grading"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h2>批阅回流</h2>
          <p>自动识别结果按试卷码和学号归档，低置信度结果不会进入学生学情。</p>
        </div>
      </div>
      {classFocus && <div className="context-filter"><span>当前只看指定教学班的批阅记录</span><button className="text-action" onClick={clearClassFocus}>查看全部班级</button></div>}
      <div className="rule-strip">
        <span>
          <CheckCircle2 />
          幂等回流
        </span>
        <span>
          <CheckCircle2 />
          学号与试卷双重匹配
        </span>
        <span>
          <CheckCircle2 />
          低于 85% 自动拦截
        </span>
        <span>
          <CheckCircle2 />
          保留原始识别证据
        </span>
      </div>
      <section className="table-panel">
        {events.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>回流时间</th>
                <th>试卷</th>
                <th>学生</th>
                <th>教学班</th>
                <th>识别置信度</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {events.data.items.map((e) => (
                <tr key={e.id}>
                  <td>{fmtDate(e.received_at)}</td>
                  <td>
                    <b>{e.paper_title}</b>
                    <small>{e.paper_code}</small>
                  </td>
                  <td>{e.student_name || e.external_student_no || "未匹配"}</td>
                  <td>{e.class_name || "—"}</td>
                  <td>
                    {e.confidence == null
                      ? "—"
                      : `${Math.round(e.confidence * 100)}%`}
                  </td>
                  <td>
                    <Status value={e.status} />
                  </td>
                  <td>
                    {e.status === "review" ? (
                      <button className="quiet small" onClick={() => {
                        setReviewing(e); setStudentNo(e.external_student_no || ""); setReviewResponses([]);
                      }}>教师复核</button>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>暂无批阅回流记录</Empty>
        )}
      </section>
      {reviewing && (
        <Modal
          title="复核批阅结果"
          subtitle="应用后才会写入作答事实与知识点掌握度；原始识别记录永久保留。"
          onClose={() => setReviewing(null)}
        >
          <div className="grading-review-body">
            <div className="grading-review-summary">
              <span>试卷<strong>{reviewing.paper_title}</strong></span>
              <span>识别置信度<strong>{reviewing.confidence == null ? "未提供" : `${Math.round(reviewing.confidence * 100)}%`}</strong></span>
              <label>学号（未匹配时必填）<input value={studentNo} onChange={(e) => setStudentNo(e.target.value)} placeholder="输入系统学号" /></label>
            </div>
            {detail.isLoading && <Empty>正在读取逐题识别证据</Empty>}
            {detail.error && <p className="form-error">{(detail.error as Error).message}</p>}
            {detail.data && <div className="grading-evidence-list">
              {detail.data.items.map((item) => {
                const response = reviewResponses.find((value) => value.position === item.position) || {};
                const expected = typeof item.answer === "object" ? (item.answer?.value ?? JSON.stringify(item.answer)) : String(item.answer ?? "—");
                return <article key={item.question_id}>
                  <header><b>{item.position}. {item.stem?.text || "题干未录入"}</b><small>{item.stable_code} · v{item.version_no}</small></header>
                  <div className="grading-answer-grid">
                    <label>识别/人工答案<input value={String(response.answer ?? "")} onChange={(e) => updateResponse(item.position, { answer: e.target.value })} /></label>
                    <span>标准答案<strong>{expected}</strong></span>
                    <label>得分<input type="number" min="0" max={item.max_score} step="0.5" value={Number(response.score ?? 0)}
                      onChange={(e) => updateResponse(item.position, { score: Number(e.target.value) })} /></label>
                    <label className="correct-check"><input type="checkbox" checked={Boolean(response.is_correct)}
                      onChange={(e) => updateResponse(item.position, { is_correct: e.target.checked,
                        score: e.target.checked ? item.max_score : Math.min(Number(response.score || 0), item.max_score) })} />判为正确</label>
                  </div>
                </article>;
              })}
            </div>}
            <label className="grading-comment">复核说明<textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="说明答案或分数调整依据；拒绝时说明原因" /></label>
          </div>
          <footer className="modal-actions modal-footer">
            <button className="danger" disabled={review.isPending} onClick={() => review.mutate("reject")}>拒绝本次识别</button>
            <button className="primary" disabled={review.isPending || detail.isLoading || !detail.data || reviewResponses.length !== detail.data.expected_count}
              onClick={() => review.mutate("apply")}>确认逐题结果并写入学情</button>
          </footer>
        </Modal>
      )}
    </>
  );
}

function LearningPage({ token, initialStudent = "", notify }: { token: string; initialStudent?: string; notify: (s: string) => void }) {
  const qc = useQueryClient();
  const [term, setTerm] = useState("");
  const [student, setStudent] = useState(initialStudent);
  useEffect(() => {
    if (initialStudent) setStudent(initialStudent);
  }, [initialStudent]);
  const search = useQuery({
    queryKey: ["students", term, token],
    enabled: !!token && term.trim().length > 0,
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          name: string;
          external_student_no?: string;
          grade?: string;
          campus_name: string;
          attempt_count: number;
        }>;
      }>(
        `/api/teaching-engine/learning/students?query=${encodeURIComponent(term)}`,
        { token },
      ),
  });
  const profile = useQuery({
    queryKey: ["profile", student, token],
    enabled: !!student,
    queryFn: () =>
      api<any>(`/api/teaching-engine/learning/students/${student}`, { token }),
  });
  const generateReport = useMutation({
    mutationFn: () => {
      const end = new Date();
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      return post(
        "/api/teaching-engine/agent/runs",
        {
          run_type: "report",
          target_type: "student",
          target_id: student,
          state: {
            student_id: student,
            report_type: "weekly",
            period_start: start.toISOString().slice(0, 10),
            period_end: end.toISOString().slice(0, 10),
          },
        },
        token,
      );
    },
    onSuccess: () => notify("周学情报告已进入教学助理审校"),
    onError: (e) => notify((e as Error).message),
  });
  const publishReport = useMutation({
    mutationFn: (id: string) => post(`/api/teaching-engine/learning/reports/${id}/publish`, {}, token),
    onSuccess: () => {
      notify("学情报告已发布");
      qc.invalidateQueries({ queryKey: ["profile", student] });
    },
    onError: (e) => notify((e as Error).message),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h2>学生学情</h2>
          <p>按学生查看作答证据、知识点掌握度、趋势和不确定度。</p>
        </div>
      </div>
      <div className="student-layout">
        <section className="student-search">
          <label>
            <Search />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="输入姓名或学号"
            />
          </label>
          {search.data?.items.map((s) => (
            <button
              key={s.id}
              className={student === s.id ? "selected" : ""}
              onClick={() => setStudent(s.id)}
            >
              <div className="avatar">{s.name.slice(0, 1)}</div>
              <div>
                <b>{s.name}</b>
                <span>
                  {s.grade || "未设年级"} · {s.campus_name}
                </span>
              </div>
              <small>{s.attempt_count} 次作答</small>
            </button>
          ))}
        </section>
        <section className="profile">
          {!profile.data ? (
            <Empty>搜索并选择一名学生</Empty>
          ) : (
            <>
              <header>
                <div className="avatar large">
                  {profile.data.student.name.slice(0, 1)}
                </div>
                <div>
                  <h3>{profile.data.student.name}</h3>
                  <p>
                    {profile.data.student.grade} ·{" "}
                    {profile.data.student.campus_name} ·{" "}
                    {profile.data.student.external_student_no}
                  </p>
                </div>
                <div className="profile-metrics">
                  <span>
                    <b>{profile.data.summary.attempts}</b>作答
                  </span>
                  <span>
                    <b>
                      {profile.data.summary.accuracy == null
                        ? "—"
                        : Math.round(profile.data.summary.accuracy * 100) + "%"}
                    </b>
                    正确率
                  </span>
                  <span>
                    <b>{profile.data.summary.weak_points}</b>薄弱点
                  </span>
                </div>
                <button
                  className="primary"
                  disabled={!token || generateReport.isPending}
                  onClick={() => generateReport.mutate()}
                >
                  <Sparkles />
                  {generateReport.isPending ? "提交中" : "生成周报告"}
                </button>
              </header>
              <h4>知识点掌握</h4>
              <div className="mastery-list">
                {profile.data.mastery.length ? (
                  profile.data.mastery.map((m: any) => (
                    <article key={m.knowledge_point_id}>
                      <div>
                        <b>{m.name}</b>
                        <span>
                          {m.subject} · {m.attempts} 次证据 · 不确定度{" "}
                          {Math.round(m.uncertainty * 100)}%
                        </span>
                      </div>
                      <strong>{Math.round(m.mastery_score * 100)}%</strong>
                      <i>
                        <em style={{ width: `${m.mastery_score * 100}%` }} />
                      </i>
                    </article>
                  ))
                ) : (
                  <Empty>尚无足够的作答证据</Empty>
                )}
              </div>
              <div className="report-section">
                <div className="section-title">
                  <div>
                    <h4>学情报告历史</h4>
                    <p>系统根据真实作答整理叙述；审批与发布分为两个独立动作。</p>
                  </div>
                </div>
                <div className="report-list">
                  {profile.data.reports?.length ? (
                    profile.data.reports.map((report: any) => (
                      <article key={report.id}>
                        <header>
                          <div>
                            <b>{report.period_start} 至 {report.period_end}</b>
                            <span>{report.report_type} · {fmtDate(report.generated_at)}</span>
                          </div>
                          <Status value={report.status} />
                        </header>
                        <p>{report.narrative?.summary || "暂无报告摘要"}</p>
                        <div className="report-metrics">
                          <span>作答 <b>{report.metrics?.attempts ?? 0}</b></span>
                          <span>正确率 <b>{report.metrics?.accuracy == null ? "—" : Math.round(report.metrics.accuracy * 100) + "%"}</b></span>
                          <span>薄弱点 <b>{report.metrics?.weak_points ?? 0}</b></span>
                        </div>
                        {report.status === "approved" && (
                          <button className="primary small" onClick={() => publishReport.mutate(report.id)}>
                            发布给学生
                          </button>
                        )}
                      </article>
                    ))
                  ) : (
                    <Empty>尚无已归档报告；生成后需先在“教学助理审校”批准</Empty>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}

const reviewFieldLabel: Record<string, string> = {
  difficulty: "整题难度", question_type: "题型", confidence: "分类置信度", reason: "分类依据",
  needs_review: "需要重点复核", tags: "题目标签", concepts: "核心概念", keywords: "检索关键词",
  solution_strategy: "解题策略", misconceptions: "常见误区", cognitive_level: "认知层级",
  normalized_text: "检索摘要", hit_strength: "知识点命中度", foundation_weight: "基础度",
  frequency_score: "常见频率", knowledge_difficulty: "知识点难度", role: "关系角色",
};
const questionTypeLabel: Record<string, string> = { single: "单选题", multiple: "多选题", judge: "判断题", fill: "填空题",
  short_answer: "简答题", essay: "作文/论述题", calculation: "计算题", composite: "综合题" };
const relationRoleLabel: Record<string, string> = { primary: "主要知识点", secondary: "次要知识点", prerequisite: "前置知识", distractor: "干扰项相关" };

function readableValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(readableValue).filter(x => x !== "—").join("、") || "—";
  if (typeof value === "object") {
    const item = value as Record<string, unknown>;
    return readableValue(item.text ?? item.value ?? item.answer ?? item.accepted_values ?? Object.values(item));
  }
  return String(value);
}

function ClassificationReview({ run }: { run: AgentRun }) {
  const proposal = (run.proposal || {}) as Record<string, any>;
  const relations = Array.isArray(proposal.knowledge_relations) ? proposal.knowledge_relations : [];
  const semantic = proposal.semantic_profile && typeof proposal.semantic_profile === "object" ? proposal.semantic_profile : {};
  return <div className="classification-review">
    <section className="review-question"><span>{run.target_subject || "学科"} · {run.target_grade || "未设年级"} · {run.target_scope_name || "课程范围"}</span>
      <h3>{readableValue(run.target_stem) || "待分类题目"}</h3></section>
    <div className="classification-summary">
      <article><small>建议题型</small><b>{questionTypeLabel[proposal.question_type] || readableValue(proposal.question_type)}</b></article>
      <article><small>整题难度</small><b>{Math.round(Number(proposal.difficulty || 0) * 100)}%</b></article>
      <article><small>分类置信度</small><b>{Math.round(Number(proposal.confidence || 0) * 100)}%</b></article>
      <article><small>人工复核</small><b>{proposal.needs_review ? "需要重点核对" : "常规核对"}</b></article>
    </div>
    <section className="review-reason"><b>系统为什么这样判断</b><p>{readableValue(proposal.reason)}</p>
      <div className="review-tags">{(proposal.tags || []).map((tag: unknown, index: number) => <span key={index}>{readableValue(tag)}</span>)}</div></section>
    <section className="relation-review"><header><div><h4>知识点命中与三轴位置</h4><p>逐项核对命中度、基础度、频率和难度，不需要查看任何内部数据格式。</p></div></header>
      {relations.map((relation: Record<string, any>, index: number) => <article key={relation.knowledge_point_id || index}>
        <div className="relation-title"><b>{relation.knowledge_point_name || `知识点 ${index + 1}`}</b><span>{relation.knowledge_point_code || ""}</span><em>{relationRoleLabel[relation.role] || relation.role || "相关知识点"}</em></div>
        <div className="axis-grid">{[["命中度", relation.hit_strength], ["基础度", relation.foundation_weight], ["常见频率", relation.frequency_score], ["知识点难度", relation.knowledge_difficulty], ["可信度", relation.confidence]].map(([label, value]) =>
          <div key={String(label)}><span>{label}</span><i><u style={{ width: `${Math.round(Number(value || 0) * 100)}%` }} /></i><b>{Math.round(Number(value || 0) * 100)}%</b></div>)}</div>
        <p>{readableValue(relation.reason)}</p>
      </article>)}
      {!relations.length && <Empty>系统没有给出可审批的知识点关系，请退回重做</Empty>}
    </section>
    <section className="semantic-review"><h4>用于后续检索的题目画像</h4><div>{Object.entries(semantic).map(([key, value]) =>
      <article key={key}><small>{reviewFieldLabel[key] || key}</small><span>{readableValue(value)}</span></article>)}</div></section>
  </div>;
}

function HumanProposalReview({ proposal }: { proposal: Record<string, unknown> }) {
  const entries = Object.entries(proposal).filter(([key]) => !["knowledge_point_id", "question_id"].includes(key));
  return <div className="human-proposal">{entries.map(([key, value]) => <section key={key}><b>{reviewFieldLabel[key] || key.replaceAll("_", " ")}</b>
    {Array.isArray(value) ? <ul>{value.map((item, index) => <li key={index}>{readableValue(item)}</li>)}</ul> : <p>{readableValue(value)}</p>}</section>)}</div>;
}

function AutomationPage({
  token,
  allCampuses,
  notify,
}: {
  token: string;
  allCampuses: boolean;
  notify: (s: string) => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<AgentRun | null>(null);
  const [comment, setComment] = useState("");
  const runs = useQuery({
    queryKey: ["agent-runs", token, allCampuses],
    enabled: !!token,
    queryFn: () =>
      api<{ items: AgentRun[] }>(`/api/teaching-engine/agent/runs${allCampuses ? "?scope=all" : ""}`, { token }),
    refetchInterval: 5000,
  });
  const maintenance = useQuery({
    queryKey: ["agent-maintenance", token],
    enabled: !!token,
    queryFn: () =>
      api<{ spaces: MaintenanceSpace[] }>(
        "/api/teaching-engine/agent/maintenance",
        { token },
      ),
    refetchInterval: 10000,
  });
  const trigger = useMutation({
    mutationFn: (code: string) =>
      post<{ queued: number }>(
        "/api/teaching-engine/agent/maintenance/trigger",
        { subject_space_code: code },
        token,
      ),
    onSuccess: (d) => {
      notify(`已请求整理 ${d.queued} 道待处理题目`);
      qc.invalidateQueries({ queryKey: ["agent-maintenance"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  const decide = useMutation({
    mutationFn: ({ id, decision, comment }: { id: string; decision: string; comment: string }) =>
      post(
        `/api/teaching-engine/agent/approvals/${id}${allCampuses ? "?scope=all" : ""}`,
        { decision, comment },
        token,
      ),
    onSuccess: () => {
      notify("审批结果已保存");
      setSelected(null); setComment("");
      qc.invalidateQueries({ queryKey: ["agent-runs"] });
      qc.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (e) => notify((e as Error).message),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h2>教学助理审校</h2>
          <p>集中处理题目分类、查重和报告建议；所有正式发布动作都由教师确认。</p>
        </div>
        <div className="model-badge">
          <ShieldCheck />
          教师审批后生效
        </div>
      </div>
      <section className="maintenance-grid">
        {maintenance.data?.spaces.map((space) => (
          <article className="maintenance-card" key={space.code}>
            <header>
              <div>
                <b>{space.subject}独立向量空间</b>
                <small>{space.collection_name}</small>
              </div>
              <button
                className="text-action"
                disabled={!space.pending || trigger.isPending}
                onClick={() => trigger.mutate(space.code)}
              >
                <RefreshCw />立即整理
              </button>
            </header>
            <div>
              <span><strong>{space.knowledge_points}</strong>知识点</span>
              <span><strong>{space.knowledge_vectors}</strong>知识向量</span>
              <span><strong>{space.questions}</strong>题目</span>
              <span><strong>{space.pending + space.batched + space.processing}</strong>队列</span>
              <span><strong>{space.duplicate_review}</strong>待查重</span>
            </div>
            <p>
              满 {space.maintenance_batch_size} 条或等待 {Math.round(space.maintenance_max_wait_seconds / 60)} 分钟自动触发；
              学科之间禁止互相召回。
            </p>
          </article>
        ))}
      </section>
      <section className="table-panel">
        {runs.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>任务</th>
                <th>状态</th>
                <th>处理方式</th>
                <th>耗时</th>
                <th>更新时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <b>
                      {r.run_type === "question_classification"
                        ? "题目分类与向量构建"
                        : r.run_type === "class_quiz"
                          ? "小测候选题"
                        : r.run_type === "report"
                          ? "学情报告"
                          : "教学自动化"}
                    </b>
                    <small>{r.id}</small>
                    <small>发起人：{r.creator_name || "系统"}</small>
                  </td>
                  <td>
                    <Status value={r.status} />
                    {r.error?.message && (
                      <small className="error-text">{r.error.message}</small>
                    )}
                  </td>
                  <td>{r.model_call?.model ? "教学助理" : "规则任务"}</td>
                  <td>
                    {r.model_call?.latency_ms
                      ? `${r.model_call.latency_ms} ms`
                      : "—"}
                  </td>
                  <td>{fmtDate(r.updated_at)}</td>
                  <td>
                    {r.approval_status === "pending" && r.approval_id ? (
                      <button className="text-action" onClick={() => { setSelected(r); setComment(""); }}>
                        查看并审批
                      </button>
                    ) : (
                      <span className="muted">查看记录</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>暂无自动化任务</Empty>
        )}
      </section>
      {selected?.approval_id && <Modal title={selected.run_type === "class_quiz" ? "审校候选题" : "审校教学助理建议"}
        subtitle={selected.run_type === "report" ? "报告指标来自数据库；批准只归档报告，仍需在学生学情页单独发布" :
          "请先核对内容与知识点；批准生成仅进入待审题库，不等于正式发布"} onClose={() => setSelected(null)}>
        <div className="approval-review">
          {selected.run_type === "class_quiz" ? <>
            <div className="proposal-summary"><b>{String(selected.proposal?.title || "课堂小测")}</b>
              <span>{String(selected.proposal?.summary || "请逐题检查题干、答案、解析和知识点")}</span></div>
            <div className="proposal-questions">{((selected.proposal?.questions as Array<Record<string, any>>) || []).map((q, index) =>
              <article key={index}><header><b>{index + 1}. {String(q.stem?.text || "")}</b><Status value="review" /></header>
                {Array.isArray(q.options) && q.options.length > 0 && <ol>{q.options.map((x: unknown, i: number) => <li key={i}>{String(x)}</li>)}</ol>}
                <p><strong>答案：</strong>{readableValue(q.answer)}</p>
                <p><strong>解析：</strong>{String(q.explanation?.text || "—")}</p>
                <small>难度 {Math.round(Number(q.difficulty || 0) * 100)}% · {Array.isArray(q.tags) ? q.tags.join("、") : ""}</small>
              </article>)}</div>
          </> : selected.run_type === "report" ? <div className="report-proposal">
            <h3>{String(selected.proposal?.summary || "学情报告")}</h3>
            {[["优势", selected.proposal?.strengths], ["薄弱点", selected.proposal?.weaknesses],
              ["建议", selected.proposal?.recommendations]].map(([label, values]) =>
              <section key={String(label)}><b>{String(label)}</b><ul>
                {(Array.isArray(values) ? values : []).map((item, i) => <li key={i}>{String(item)}</li>)}
              </ul></section>)}
          </div> : selected.run_type === "question_classification" ? <ClassificationReview run={selected} /> :
            <HumanProposalReview proposal={selected.proposal || {}} />}
          <label className="approval-comment">审批意见<textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="要求重新生成时必须填写具体调整意见" /></label>
        </div>
        <footer className="modal-actions"><button className="quiet danger" disabled={decide.isPending}
          onClick={() => decide.mutate({ id: selected.approval_id!, decision: "rejected", comment: comment || "教师拒绝该建议" })}>拒绝</button>
          <button className="quiet" disabled={decide.isPending || !comment.trim()}
            onClick={() => decide.mutate({ id: selected.approval_id!, decision: "revision_requested", comment })}>按意见重做</button>
          <button className="primary" disabled={decide.isPending}
            onClick={() => decide.mutate({ id: selected.approval_id!, decision: "approved", comment: comment || "教师已核对并批准" })}>
            {selected.run_type === "report" ? "批准归档报告" : selected.run_type === "question_classification" ? "批准应用分类" : "批准进入待审题库"}
          </button></footer>
      </Modal>}
    </>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5000, retry: 1, refetchOnWindowFocus: false },
  },
});
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
